/**
 * Insights Section Controller
 * Manages the Library page UI — Topics (hidden, deep-link only), Patterns,
 * Assessments, and Guides tabs. Formerly two separate pages (`/library` with
 * the Patterns/Assessments/Guides tabs, and a Topics-only `/insights`);
 * merged onto this single surviving implementation, which already carried
 * the tab scaffolding (page toggle, per-tab orientation banners/info copy/
 * filter bars) a 4-tab page needs. `topics` is first in tab order but
 * renders no page-toggle button — it's reachable only via the `?topic=<id>`
 * deep link.
 */

import page from '../vendor/page.js';
import { escapeHtml } from '../core/utils.js';
import { API } from '../core/api.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import {
  emitContentViewed,
  emitContentStarred,
  emitContentArchived,
  CONTENT_STARRED,
  CONTENT_ARCHIVED,
  CONTENT_DELETED
} from '../core/contentSync.js';
import { Tooltip } from '../utils/Tooltip.js';
import { StickyStack } from '../utils/StickyStack.js';
import { OrientationBanner } from '../components/OrientationBanner.js';
import { ResultsFilterBar } from '../components/ResultsFilterBar.js';
import { renderEmptyState } from '../components/emptyState.js';
import { wireDisclosures } from '../components/disclosure.js';
import { renderNavBar as renderSegmentNavBar, renderNavItem as renderSegmentNavItem } from '../components/segmentNav.js';
import { buildPageInfoPanel, renderPageInfoTitle, renderPageInfoFaq } from '../components/pageInfoPanel.js';
import { openSessionInPanel } from '../components/sessionDrillHelper.js';
import { PATTERN_INSTANCES_ZONE, launchPatternDiscussion, renderPatternDetailBody } from '../components/patternDrillHelper.js';
import { renderRowKebab, RowMenuController } from '../components/optionsMenu.js';
import { SessionsSection } from './SessionsSection.js';
import { buildAssessmentDetailContent } from '../chat/structuredBlockRenderers.js';
import { formatTimeAgo, formatRelativeDate } from '../utils/timeFormat.js';
import { notifyArchived, notifyArchiveFailed } from '../utils/archiveFeedback.js';
import {
  armGuideContentsSpy,
  buildMergedGuideDetailPayload,
  buildMergedGuideContentsMenu,
  contentsGroups,
  patchGuideContentsRail,
  renderGuideYouPart,
  patchMergedGuideStamp,
  patchMergedGuideGate,
  wireMergedGuideBody,
  isTogetherReadable,
  isTogetherUnread,
  GUIDE_PART_YOU_ID,
  GUIDE_PART_TOGETHER_ID,
  GUIDE_TOGETHER_BAND_ID
} from '../components/guideDrillHelper.js';
import { sharedGuideCardQualifier } from '../components/sharedGuideDrillHelper.js';
import { GUIDE_PART_TOGETHER, GUIDE_PART_YOU, renderPartChip } from '../components/guidePartMark.js';

/** Filled star marker / toggle — shared sprite symbol (mirrors PracticesSection's STAR_SVG). */
const STAR_SVG = `<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><use href="#icon-star"/></svg>`;

const NAV_ITEM_SELECTOR = '.page-toggle__btn';
const NAV_ACTIVE_CLASS = 'page-toggle__btn--active';

/**
 * Module-level launch-in-flight guard for `handleDiscussAssessment` — mirrors
 * `guideDrillHelper.js`'s `atomLaunchInFlight`. Module-level (not
 * instance-level) because it only needs to survive a single event-loop turn,
 * not the section's lifecycle. The pattern launch keeps its own parallel flag
 * inside `patternDrillHelper.js` (`launchPatternDiscussion`) rather than
 * sharing this one, so a double-click on a pattern card can never suppress a
 * genuine click on a framework card (or vice versa).
 */
let frameworkDiscussionLaunchInFlight = false;

export class InsightsSection {
  /**
   * Tab the page lands on with no `?tab=` param, and the fallback whenever the
   * hidden `topics` tab has to hand control back to a visible pill.
   */
  static DEFAULT_TAB = 'guides';

  /**
   * URL parameter schema for deep linking and filter state.
   *
   * - `tab`: which tab is active. Pushed via `page.replace` on tab clicks.
   *   `topics` is a valid enum value but renders no page-toggle button — it's
   *   reachable only through the `topic` param (see applyURLParams).
   * - `topic`: taxonomy topic id whose summary detail panel is open.
   * - `pattern`: pattern code (slug, e.g. 'avoidant-withdrawal').
   * - `guide`: guide definition id (slug, e.g. 'guide-values-vision'). The one
   *   canonical guide deep link — it opens the merged reader for whichever
   *   halves of that definition are readable (D10, D19).
   * - `sharedGuide`: **deprecated alias for `guide`** (D10). Kept as a schema
   *   key because `URLParamsHandler.parse` reads schema keys ONLY — dropping
   *   it here would silently discard the links `SharingDetailPanel` and the
   *   backend's `frozenConsumersForViewer` still emit. `applyURLParams`
   *   normalises it to `?guide=<id>` and remembers that the link asked for the
   *   couple's part; nothing resolves it against `this.sharedGuides` any more.
   * - `framework`: clinical questionnaire id for an assessment result.
   * The four distinct detail params are mutually exclusive — only one detail
   * panel is open at a time.
   */
  static urlParamSchema = {
    tab: {
      type: 'enum',
      enum: ['topics', 'patterns', 'assessments', 'guides'],
      default: InsightsSection.DEFAULT_TAB
    },
    topic: {
      type: 'string',
      default: null
    },
    pattern: {
      type: 'string',
      default: null
    },
    guide: {
      type: 'string',
      default: null
    },
    sharedGuide: {
      type: 'string',
      default: null
    },
    framework: {
      type: 'string',
      default: null
    }
  };

  /** Recency window (days) after which a pattern's dot goes dormant. */
  static ACTIVE_WINDOW_DAYS = 20;

  /**
   * @param {Object} elements - DOM element references
   * @param {HTMLElement} elements.container - Section container element
   * @param {EventBus} eventBus - Event bus for module communication
   */
  constructor(elements, eventBus) {
    this.container = elements.container;
    this.eventBus = eventBus;

    // State
    this.isActive = false;
    this.isInitialized = false;
    this.currentTab = InsightsSection.DEFAULT_TAB;
    this.orientationBanner = null;

    // Sticky header stack (title → segment nav → the active tab's filter bar)
    this.stickyStack = null;

    // Topics state (the 12 taxonomy topics, sourced from discovery progress).
    // Hidden tab — no page-toggle button — reachable only via `?topic=<id>`.
    this.discoveryAPI = new DiscoveryAPI();
    this.topics = [];
    this.filteredTopics = [];
    this.topicsLoading = false;
    this.topicsError = null;
    this.topicScope = 'all'; // 'all' | 'core' | 'life'
    this.topicSortOrder = 'default'; // 'default' | 'most' | 'least'
    this.topicsFilterBar = null;

    // Patterns state
    this.patterns = [];
    this.filteredPatterns = [];
    this.filterBar = null;
    this.patternsLoading = false;
    this.patternsError = null;
    this.selectedPatternData = null;

    // Education panel state
    this.educationPatternCode = null;
    this.educationPatternData = null;
    this.educationLoading = false;

    // Guides state
    this.guides = [];
    this.filteredGuides = [];
    // Shared-guide manifest — the resolution source for `guideDefinitionId`
    // lookups, and NEVER merged into `this.guides`. `applyGuidesFilters`
    // derives one merged row per definition from the two arrays, so the tab
    // renders one list with one count while each half keeps its own lifecycle,
    // its own gate, and its own write path (D1). `_runGuideBulk` still only
    // ever reads `this.guides`, which is what keeps star (a `UserGuide`
    // action) structurally unable to touch the couple's artifact.
    // `this._partner` is the couple partner's profile
    // (firstName/name/username), fetched lazily once shared guides exist —
    // used for the reader's partner-facing copy — and cached for the section's
    // lifetime (unlike guides/patterns/topics, a partner's display name
    // doesn't go stale within a session, so it survives deactivate()).
    this.sharedGuides = [];
    // Explicit "both guide fetches have settled" sentinel for the
    // `sharedGuide` URL-scrub check — `this.guides.length > 0` (the existing
    // idiom the `guide` param branch uses) is wrong for this one: an account
    // with genuinely zero own guides would leave `this.guides` empty forever,
    // so a stale `?sharedGuide=<missing>` would never scrub. Set true only on
    // loadGuidesData's success path (mirrors `frameworksLoaded`); never
    // reverts to false except on section re-activation.
    this._guidesTabLoaded = false;
    this._partner = null;
    this._partnerLoading = false;
    this.guidesSortOrder = 'newest';
    this.guidesStatusFilter = 'active'; // 'active' | 'archived' — the two views are disjoint
    this.guidesStarredFilter = false;
    this.guidesFilterBar = null;
    this.guidesLoading = false;
    this.guidesError = null;
    this.selectedGuide = null;
    this.generatingGuideId = null;
    /** Row options menu open/close controller for guide nav cards (menu is a child of its `.guide-nav-card`). */
    this._guideRowMenu = new RowMenuController({
      container: () => this.container,
      keyAttr: 'data-guide-id',
      rowSelector: '.guide-nav-card',
      hoverClose: true,
    });

    // Frameworks (assessment results) state. `frameworksLoaded` — not
    // `frameworks.length` — gates URL scrubbing, so a loaded-but-empty list
    // still cleans stale `?framework=` params.
    this.frameworks = [];
    this.filteredFrameworks = [];
    this.frameworkSortOrder = 'newest';
    this.frameworksFilterBar = null;
    this.frameworksLoaded = false;
    this.frameworksLoading = false;
    this.frameworksError = null;

    // Right panel detail state
    this._panelOpen = false;
    this._panelDetail = null; // { type: 'topic' | 'pattern' | 'guide' | 'framework', id: string } | null
    this._detailContentEl = null;

    // Observed-instances zone open/closed, remembered per panel open so the
    // async instance load (`loadPatternDetail`) re-renders into the state the
    // user left rather than snapping back to the default.
    this._patternInstancesExpanded = true;

    // Transient carrier for the deprecated `?sharedGuide=` alias (D10): the
    // alias branch of applyURLParams sets it to 'together' and rewrites the URL
    // to `?guide=<id>`; the `guide` branch of the resulting dispatch consumes
    // and clears it. There is deliberately no `part` URL param.
    this._pendingGuidePart = null;

    // Mark-seen for the couple's half (D5). ONE IntersectionObserver on a
    // bounded sentinel (the "Together added" band, else the Together part's
    // first section head) — never on the whole part, which a long reading would
    // never intersect at a fraction threshold. The in-flight flag makes the
    // observer and the "Got it" button share a single write.
    this._togetherObserver = null;
    this._togetherSeenInFlight = false;

    // Disposer for the contents rail's scroll-spy (one per open reader). The
    // host owns it so the payload's `onDispose`, a superseding open and
    // `deactivate` all tear down the SAME observer. The content node it was
    // armed over is remembered beside it so a LATE disposer (the panel's
    // cross-fade disposes the outgoing payload 130ms after the next one armed)
    // can only tear down its own spy — see `_disposeContentsSpyFor`.
    this._contentsSpyDisposer = null;
    this._contentsSpyContentEl = null;

    this.eventBus?.on?.('badge:state-changed', () => {
      if (!this.isActive) return;
      this.render();
      if (this.currentTab === 'topics') this._attachTopicsTabListeners();
      if (this.currentTab === 'patterns') this._attachPatternsTabListeners();
      if (this.currentTab === 'guides') this._attachGuidesTabListeners();
      if (this.currentTab === 'assessments') this._attachFrameworksTabListeners();
    });
  }

  /**
   * Initialize the section (called once on first activation)
   */
  initialize() {
    if (this.isInitialized) return;

    // Universal cross-surface sync bridge (contentSync). /insights holds only
    // `guide` items (keyed by guideDefinitionId, same as the sidebar and
    // /search), stored with BOOLEAN flags alongside their timestamps — patch
    // both. Star and status are filter axes here, so every patch re-runs
    // `applyGuidesFilters()` rather than just re-rendering markers. Handlers
    // are idempotent, which is what makes this section's own `_runGuideBulk`
    // emits self-skip.
    this.eventBus.on(CONTENT_STARRED, ({ itemType, itemId, starred } = {}) => {
      if (itemType !== 'guide') return;
      // Panel header first — before the list early-returns — so a foreign star
      // of the open guide still flips the visible header star.
      if (this._panelOpen && this._panelDetail?.type === 'guide' && this._panelDetail.id === itemId) {
        this.eventBus.emit('right-panel:set-star', { starred });
      }
      const guide = this.guides.find(g => g.id === itemId);
      if (!guide || Boolean(guide.starred) === starred) return;
      guide.starred = starred;
      guide.starredAt = starred ? new Date().toISOString() : null;
      if (this.selectedGuide?.id === itemId) {
        this.selectedGuide = { ...this.selectedGuide, starred, starredAt: guide.starredAt };
      }
      this.applyGuidesFilters();
      if (this.isActive) this.updateGuidesList();
    });

    this.eventBus.on(CONTENT_ARCHIVED, ({ itemType, itemId, archived } = {}) => {
      if (itemType !== 'guide') return;
      const guide = this.guides.find(g => g.id === itemId);
      if (!guide || Boolean(guide.archived) === archived) return;
      guide.archived = archived;
      guide.archivedAt = archived ? new Date().toISOString() : null;
      if (this.selectedGuide?.id === itemId) {
        this.selectedGuide = { ...this.selectedGuide, archived, archivedAt: guide.archivedAt };
      }
      this.applyGuidesFilters();
      if (this.isActive) this.updateGuidesList();
      // The status axis is disjoint, so an archive flip moves the row out of
      // the current list. Rebuild the open panel's header so its
      // Archive/Unarchive label matches (mirrors `_handleGuidePanelOption`).
      this._rebuildOpenGuidePanel(itemId);
    });

    // No `/library` surface deletes a guide any more, but the support route
    // survives and other sections still broadcast deletes for their own types,
    // so this stays as the reconciler for a guide that vanishes underneath us.
    this.eventBus.on(CONTENT_DELETED, ({ itemType, itemId } = {}) => {
      if (itemType !== 'guide') return;
      const before = this.guides.length;
      this.guides = this.guides.filter(g => g.id !== itemId);
      if (this.guides.length === before) return;
      this.applyGuidesFilters();
      if (this.isActive) this.updateGuidesList();
      if (this._panelOpen && this._panelDetail?.type === 'guide' && this._panelDetail.id === itemId) {
        this.eventBus.emit('right-panel:close');
      }
    });

    this.eventBus.on('right-panel:closed', (data) => {
      if (typeof data?.contentType !== 'string' ||
          !(data.contentType.startsWith('library-') || data.contentType.startsWith('insights-'))) return;

      this._disconnectTogetherObserver();
      this._disposeContentsSpy();
      this._detailContentEl = null;
      this.educationPatternCode = null;
      this.educationPatternData = null;
      this.educationLoading = false;
      this.selectedGuide = null;
      this.selectedPatternData = null;
      this._panelDetail = null;
      this._panelOpen = false;
      this._clearTopicSummaryPoll();
      this.updatePatternsList();
      this.updateGuidesList();
      this.updateFrameworksList();
      this.updateTopicsList();

      // The hidden `topics` tab has no page-toggle button — closing its detail
      // panel while still on that tab would strand the user on content with
      // no way back. Return to the default/visible tab. If the user already
      // switched tabs before the panel finished closing, `currentTab` won't
      // be 'topics' anymore and this is a no-op.
      if (this.currentTab === 'topics') {
        this.handleTabChange(InsightsSection.DEFAULT_TAB, { skipUrlPush: true });
      }

      // Clean up any URL detail param when the panel closed within the
      // section. Skipped when isActive is false (cross-section nav owns
      // its own URL change).
      if (!this.isActive) return;
      const url = new URL(window.location.href);
      const hadDetailParam = url.searchParams.has('pattern') || url.searchParams.has('guide') ||
        url.searchParams.has('sharedGuide') || url.searchParams.has('framework') || url.searchParams.has('topic');
      if (hadDetailParam) {
        url.searchParams.delete('pattern');
        url.searchParams.delete('guide');
        url.searchParams.delete('sharedGuide');
        url.searchParams.delete('framework');
        url.searchParams.delete('topic');
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
    });

    this.render();
    this.isInitialized = true;
  }

  /**
   * Render the section content
   */
  render() {
    this.container.innerHTML = `
      <div class="explore-section">
        <div class="explore-container">
          <div class="explore-view--list" data-sticky-stack>
            ${renderPageInfoTitle('Library')}
            <div class="page-header content-width">
              <div class="page-header__subtitle-row">
                <p class="page-header__subtitle">What our work has uncovered</p>
              </div>
            </div>

            ${this._renderNavBar()}

            <div class="understand-section__content content-width">
              ${this.renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    `;

    this.attachEventListeners();
    this._showOrientationBanner(this.currentTab);
    this._initStickyStack();
  }

  /**
   * Render the page-toggle tab bar under the page title.
   * @returns {string} HTML string
   */
  _renderNavBar() {
    const items = `
      ${this.renderNavItem('guides', 'Guides')}
      ${this.renderNavItem('patterns', 'Patterns')}
      ${this.renderNavItem('assessments', 'Assessments')}
    `;
    return renderSegmentNavBar(items, { ariaLabel: 'Library filter' });
  }

  /**
   * Render a single nav item for the tab bar
   * @param {string} id - Tab identifier
   * @param {string} label - Display label
   * @param {number} [count] - Optional item count
   * @returns {string} HTML string
   */
  renderNavItem(id, label, count = null) {
    const isActive = this.currentTab === id;
    const indicator = window.badgeService?.getTabIndicator('insights', id);
    return renderSegmentNavItem({ id, label, count, active: isActive, indicator });
  }

  /**
   * Render tab content based on current tab
   * @returns {string} HTML string
   */
  renderTabContent() {
    switch (this.currentTab) {
      case 'topics':
        return this.renderTopicsContent();
      case 'patterns':
        return this.renderPatternsContent();
      case 'guides':
        return this.renderGuidesContent();
      case 'assessments':
        return this.renderFrameworksContent();
      default:
        return '';
    }
  }

  /**
   * Render topics content — the 12 taxonomy topics as nav cards. Hidden tab:
   * reachable only via the `?topic=<id>` deep link, with no page-toggle button.
   * Reuses the shared `.session-card` markup (same as patterns/sessions).
   * @returns {string} HTML string
   */
  renderTopicsContent() {
    if (this.topicsLoading) {
      return `
        <div class="understand-tab-content" data-tab-content="topics">
          <div class="patterns-loading">
            <div class="loading-spinner"></div>
            <p>Loading topics...</p>
          </div>
        </div>
      `;
    }

    if (this.topicsError) {
      return `
        <div class="understand-tab-content" data-tab-content="topics">
          <div class="patterns-error">
            <p>Unable to load topics. Please try again.</p>
            <button class="btn btn--secondary" data-action="retry-topics">Retry</button>
          </div>
        </div>
      `;
    }

    return `
      <div class="understand-tab-content" data-tab-content="topics">
        <div data-filter-bar-mount="topics" data-sticky-slice></div>
        <div class="session-cards-container">
          ${this._renderTopicsBody()}
        </div>
      </div>
    `;
  }

  /**
   * Topic cards, or the empty state when none are visible. Shared by the full
   * tab render and `updateTopicsList()` so a filter change that hides every
   * topic can't leave a blank container.
   * @returns {string} HTML string
   */
  _renderTopicsBody() {
    if (this.filteredTopics.length === 0) {
      return this.topics.length === 0
        ? renderEmptyState({
          title: 'No topics yet',
          description: "Topics organize what we're learning about you across the areas of your life. They'll appear here as your understanding grows."
        })
        : renderEmptyState({
          title: 'No topics match these filters',
          description: 'Try changing or resetting your filters.',
          filtered: true
        });
    }
    return this._renderTopicCards(this._activeTopicId());
  }

  /**
   * Render a single topic nav card (clickable, opens detail panel).
   * @param {Object} topic - { id, label, description, type }
   * @param {string|null} [activeId] - Topic id currently shown in the right panel
   * @returns {string} HTML string
   */
  renderTopicCard(topic, activeId = null) {
    const isOpenInPanel = Boolean(activeId) && topic.id === activeId;
    const activeCls = isOpenInPanel ? ' session-card--active' : '';
    // Blurb leads; fall back to the static description until a summary exists.
    const blurb = topic.cardBlurb || topic.description || '';
    const summaryHtml = blurb
      ? `<p class="session-card__summary">${this.escapeHtml(blurb)}</p>`
      : '';
    // "New insights" affordance — only on a written summary that has gone stale.
    const freshDot = topic.status === 'STALE'
      ? '<span class="topic-card__fresh-dot" title="New insights since this was written" aria-label="New insights available"></span>'
      : '';
    return `
      <div class="card session-card${activeCls}"
           data-topic-id="${this.escapeHtml(topic.id)}"
           role="button"
           tabindex="0"
           aria-expanded="${isOpenInPanel}">
        <div class="session-card__content">
          <div class="session-card__title-row">
            <h4 class="session-card__title">${this.escapeHtml(topic.label)}</h4>
            ${freshDot}
          </div>
          ${summaryHtml}
          ${this._renderCoverageMeter(topic)}
        </div>
        <span class="session-card__chevron" aria-hidden="true">
          <svg width="19" height="19" viewBox="0 0 16 16">
            <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
        </span>
      </div>
    `;
  }

  /**
   * Map a topic's coverage + status into a 3-dot meter level (0-3) and a
   * plain-language caption. `generating` reads as in-progress, not empty.
   * @param {Object} topic - merged topic ({ overallCoverage, status, hasData })
   * @returns {{ level: number, caption: string, generating: boolean }}
   */
  _coverageMeta(topic) {
    if (topic.status === 'GENERATING' && !topic.cardBlurb) {
      return { level: 0, caption: 'Putting this together…', generating: true };
    }
    switch (topic.overallCoverage) {
      case 'good': return { level: 3, caption: 'A clear picture', generating: false };
      case 'partial': return { level: 2, caption: 'Coming into focus', generating: false };
      default:
        return topic.hasData
          ? { level: 1, caption: 'Just getting started', generating: false }
          : { level: 0, caption: 'Not explored yet', generating: false };
    }
  }

  /**
   * Render the 3-dot coverage meter + caption row for a topic card.
   * @param {Object} topic
   * @returns {string} HTML string
   */
  _renderCoverageMeter(topic) {
    const { level, caption, generating } = this._coverageMeta(topic);
    const dots = [0, 1, 2]
      .map(i => `<span class="topic-card__dot${i < level ? ' topic-card__dot--on' : ''}"></span>`)
      .join('');
    const meterCls = `topic-card__meter${level === 0 ? ' topic-card__meter--empty' : ''}${generating ? ' topic-card__meter--generating' : ''}`;
    return `
      <div class="topic-card__coverage ${meterCls}">
        <span class="topic-card__dots" aria-hidden="true">${dots}</span>
        <span class="topic-card__coverage-label">${this.escapeHtml(caption)}</span>
      </div>
    `;
  }

  /**
   * Render the topic cards as a single flat list. The 'default' (Suggested) sort
   * keeps the core topics first (taxonomy displayOrder), so they group at the top
   * without an explicit section header.
   * @param {string|null} activeId
   * @returns {string} HTML string
   */
  _renderTopicCards(activeId) {
    return this.filteredTopics.map(t => this.renderTopicCard(t, activeId)).join('');
  }

  /**
   * Render guides tab content
   * @returns {string} HTML string
   */
  renderGuidesContent() {
    if (this.guidesLoading) {
      return `
        <div class="understand-tab-content" data-tab-content="guides">
          <div class="guides-loading">
            <div class="loading-spinner"></div>
            <p>Loading guides...</p>
          </div>
        </div>
      `;
    }

    if (this.guidesError) {
      return `
        <div class="understand-tab-content" data-tab-content="guides">
          <div class="guides-error">
            <p>Unable to load guides. Please try again.</p>
            <button class="btn btn--secondary" data-action="retry-guides">Retry</button>
          </div>
        </div>
      `;
    }

    return `
      <div class="understand-tab-content" data-tab-content="guides">
        <div data-filter-bar-mount="guides" data-sticky-slice></div>
        <div class="session-cards-container">
          ${this._renderGuidesBody()}
        </div>
      </div>
    `;
  }

  /**
   * One card per guide definition, or the empty state when none are visible.
   * Shared by the full tab render and `updateGuidesList()`.
   *
   * The no-data test counts guides that can actually RENDER — an availability
   * row that has been attempted (non-null `status`) or is
   * unlocked-and-generatable, plus every shared-guide manifest item — not
   * `this.guides.length`: the API returns one row per visible guide definition
   * with `status: null` when the guide has never been generated, and
   * `applyGuidesFilters()` drops the non-generatable ones. Testing the raw
   * array is why this tab used to render a blank container to every user with
   * no guides. Keep the count local — `this.guides.length` is also the tab's
   * "already loaded?" sentinel, and redefining it would refetch on every visit.
   *
   * Every row here is a MERGED row (`applyGuidesFilters`), so there is one
   * renderer and one card per definition: the couple's reading is never a
   * second row beside the viewer's own.
   * @returns {string} HTML string
   */
  _renderGuidesBody() {
    if (this.filteredGuides.length === 0) {
      // "Nothing yet" vs "nothing matching" is judged over BOTH sources: an
      // account whose only artifact is a couple's reading, hidden by an active
      // filter, has guides — it just has none on screen.
      const attempted = this.guides.filter(
        g => g.status || InsightsSection.canGenerateGuide(g) || InsightsSection.isLockedGuide(g)
      ).length + (this.sharedGuides?.length || 0);
      return attempted === 0
        ? renderEmptyState({
          title: 'No guides yet',
          description: 'Guides are personalized resources created from your discovery insights. Complete more discovery questionnaires to unlock guides.'
        })
        : renderEmptyState({
          title: 'No guides match these filters',
          description: 'Try changing or resetting your filters.',
          filtered: true
        });
    }

    // One panel type for the whole merged surface — the deprecated
    // `?sharedGuide=` entry is an alias onto this same definition (D10).
    const activeId = this._activeGuideId();
    return this.filteredGuides
      .map(row => this.renderGuideCard(row, activeId))
      .join('');
  }

  /**
   * Compose one guide definition's two halves into the row every `/library`
   * guide surface reads.
   *
   * Presentation-layer composition only (D1): the halves are not merged, they
   * are carried side by side. `this.guides` and `this.sharedGuides` stay the
   * only sources of truth — star and archive replace those arrays wholesale
   * and the rows are re-derived, so a row can never drift from them.
   *
   * @param {string} definitionId - Guide definition id (the card's identity)
   * @param {Object|null} individual - `getAvailableGuides()` availability row
   * @param {Object|null} shared - Shared-guide manifest item
   * @returns {{definitionId: string, individual: Object|null, shared: Object|null, archived: boolean}}
   */
  static _mergedGuideRow(definitionId, individual, shared) {
    const archived = InsightsSection._guideRowArchived(individual, shared);
    // Your archived reading stays put while the couple's half is live: the
    // card that surfaces under Active is Together-only — no You slot, no star,
    // a Together-only reader — because what you filed away must not come back
    // just because the couple's reading landed (D8). Archiving that card files
    // the shared half too, and Unarchive from the Archived list restores both.
    const ownArchived = InsightsSection._hasUserGuideRow(individual)
      && (Boolean(individual.archived) || Boolean(individual.archivedAt));
    const hideOwn = ownArchived && !archived;
    return {
      definitionId,
      individual: hideOwn ? null : (individual || null),
      shared: shared || null,
      archived
    };
  }

  /**
   * Whether a card counts as Archived: EVERY half that is present is archived
   * (D8). A half you don't have can't hold the card in Archived, which is what
   * lets a couple's reading that lands after you archived your own resurface
   * under Active as a Together-only card. Your partner's archive state is
   * never serialized and never reaches here.
   *
   * "Present" for the individual half means a `UserGuide` row exists — an
   * availability row for a definition that was never generated carries no
   * archive state of its own and must not vote. The couple's half votes when
   * it is READABLE, or when it is the only half there is (a Together-only
   * card must still file away). A pending, sealed, or withdrawn item beside
   * your own reading has no document, so it must not vote — otherwise your
   * archived reading would be hidden behind a Together half with nothing to
   * open, leaving a wordless card under Active that does nothing on click.
   * @param {Object|null} individual - Availability row
   * @param {Object|null} shared - Shared-guide manifest item
   * @returns {boolean}
   */
  static _guideRowArchived(individual, shared) {
    const halves = [];
    const ownPresent = InsightsSection._hasUserGuideRow(individual);
    if (ownPresent) {
      halves.push(Boolean(individual.archived) || Boolean(individual.archivedAt));
    }
    if (shared && (!ownPresent || isTogetherReadable(shared))) {
      halves.push(Boolean(shared.archivedAt));
    }
    return halves.length > 0 && halves.every(Boolean);
  }

  /**
   * Whether a `UserGuide` row exists for this definition at all. `status` is
   * non-null exactly when one does (see `getAvailableGuides`), so an
   * availability row for a never-generated definition is NOT a present half:
   * it carries no star, no archive state, and nothing to act on.
   * @param {Object|null} individual - Availability row
   * @returns {boolean}
   */
  static _hasUserGuideRow(individual) {
    return Boolean(individual) && (Boolean(individual.status) || individual.hasGuide === true);
  }

  /**
   * The merged row for one definition, derived on demand from the two source
   * arrays. Serves the click dispatch, the row kebab, and every panel path —
   * none of them may resolve a card out of `filteredGuides`, which is a
   * filtered VIEW and is empty for a row the active filters hide (a deep link
   * to an archived guide, say).
   * @param {string} definitionId - Guide definition id
   * @returns {Object|null} Merged row, or null when neither half exists
   */
  _guideRow(definitionId) {
    if (!definitionId) return null;
    const individual = this.guides.find(g => g.id === definitionId) || null;
    const shared = (this.sharedGuides || []).find(sg => sg.guideDefinitionId === definitionId) || null;
    if (!individual && !shared) return null;
    return InsightsSection._mergedGuideRow(definitionId, individual, shared);
  }

  /**
   * Render one guide nav card — the light-blue `.session-card` image row every
   * other nav list on the site uses: cover-page tile, title, tagline, the part
   * pills, and the `.note-row` meta cell.
   *
   * The card sorts what it says by NATURE, not by which half it belongs to.
   * The rule that decides the side: does this change what the reader should
   * do? Yes → the right rail; no → it describes a half and stays on that
   * half's pill.
   *
   *   - The part pills (`renderGuideStatusRow`) say which halves the guide has
   *     — a solo card carries a single You pill, a coupled card You then
   *     Together — every pill filled in its part hue, with at most one
   *     lowercase DESCRIPTIVE qualifier: (writing) / (waiting) / (hidden).
   *     `writing` lands here by that test: there is nothing to do about it.
   *   - The rail (`_guideRailState`) is at most ONE ACTIONABLE word in the
   *     app's severity inks — Error / Needs review / Generate / New — in the
   *     slot the date otherwise occupies.
   *
   * The meta cell therefore renders EITHER the rail word (`.note-row__status`
   * wrapping a `.notification-label`, the wrapper content-row.css keys its
   * hover swap and menu-open pin on) OR the newest half's date
   * (`.note-row__date`), never both. That is what lets the right column mean
   * exactly one thing — something there needs you — while an empty one is a
   * row at rest. An actionable row hides its date but is still sorted by it
   * (`_guideSortKey`). The tile's corner dot is the rail's colour at a glance:
   * no rail word, no dot — and the gray Locked word, which needs nothing from
   * the reader here, lights none either (`_railDotTone`).
   *
   * Card framing lives in `features/reflections.css` under GUIDES TAB — NAV
   * CARD, scoped to `#insights-page` because /discovery's path-detail card
   * carries `.guide-nav-card` on the artifact shell.
   *
   * The artwork filename is emitted via `encodeURIComponent`, not `escapeHtml`,
   * because it is a URL path segment rather than plain HTML attribute text.
   *
   * @param {Object} row - Merged guide row (see `_mergedGuideRow`)
   * @param {string|null} [activeId] - Definition id currently shown in the right panel
   * @returns {string} HTML string
   */
  renderGuideCard(row, activeId = null) {
    const { definitionId, individual, shared } = row;
    const isOpenInPanel = Boolean(activeId) && definitionId === activeId;
    const activeCls = isOpenInPanel ? ' session-card--active' : '';
    const status = this._effectiveIndividualStatus(row);

    // Star is a static marker, NOT a button — it appears only when the guide is
    // starred (mirrors /search's session cards). Star/Unstar itself happens via
    // the detail panel's Options dropdown and the row kebab. It reads the
    // `UserGuide` row only: star acts on your reading, never on the couple's.
    const star = individual?.starred
      ? `<span class="note-row__star" aria-label="Starred">${STAR_SVG}</span>`
      : '';

    const options = this.renderGuideRowOptions(row);
    const title = individual?.title || shared?.title || 'Guide';
    const tagline = individual?.tagline || individual?.subtitle || '';
    const date = InsightsSection._guideRowDate(row);
    const rail = InsightsSection._guideRailState(row, status);
    const statusRow = this.renderGuideStatusRow(row, status);
    let meta = '';
    if (rail) {
      meta = `<span class="note-row__status"><span class="notification-label notification-label--${rail.color}">${rail.word}</span></span>`;
    } else if (date) {
      meta = `<span class="note-row__date">${this.escapeHtml(date)}</span>`;
    }

    return `
      <div class="card session-card guide-nav-card note-row${activeCls}"
           data-guide-id="${this.escapeHtml(definitionId)}"
           role="button"
           tabindex="0"
           aria-expanded="${isOpenInPanel}">
        ${InsightsSection.renderGuideTile(individual?.cardContent?.image, InsightsSection._railDotTone(rail))}
        <div class="session-card__content">
          <div class="session-card__title-row">
            ${star}
            <h4 class="session-card__title">${this.escapeHtml(title)}</h4>
          </div>
          ${tagline ? `<p class="session-card__summary">${this.escapeHtml(tagline)}</p>` : ''}
          ${statusRow}
        </div>
        <span class="note-row__meta">
          ${meta}
          ${options}
          <span class="session-card__chevron note-row__chevron" aria-hidden="true">
            <svg width="19" height="19" viewBox="0 0 16 16">
              <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
          </span>
        </span>
      </div>
    `;
  }

  /**
   * The cover-page tile every /library guide row leads with: a closed portrait
   * page (white sheet, hairline, two ruled lines) carrying the shared
   * `.path-disc` — the same circle the /discovery rows and orbit draw — as its
   * cover art. The page says "document"; the disc says which path the
   * document came from. Distinct from the tilted, bottom-bleeding artifact
   * tile the chat announcement card and /discovery's path-detail card share:
   * that one needs a bounded card edge to clip against, and this list row has
   * none, so the page here closes on all four sides.
   *
   * White-grounded by design (see the CSS): the row itself washes blue on
   * hover and darker blue when active, so a page tinted from that same family
   * would dissolve into it. A Together-only card carries no path artwork and
   * puts the book glyph where the disc would be — a document without a path,
   * which is what such a guide is — so every row keeps one height and one
   * left edge.
   *
   * The corner dot sits inset in the page's top-right corner and is
   * decorative (`aria-hidden`): it is the rail's colour at a glance
   * (`_guideRailState`), and the rail word carries the meaning, so it adds
   * nothing for a screen reader. No rail word → no dot; a pill qualifier
   * such as (writing) is descriptive, not actionable, and lights nothing; nor
   * does the gray Locked word (`_railDotTone`).
   *
   * @param {string} [image] - `cardContent.image` filename, when the path has artwork
   * @param {'blue'|'orange'|'red'|null} [dotTone=null] - The rail's colour; null for no dot
   * @returns {string} HTML string
   */
  static renderGuideTile(image, dotTone = null) {
    const inner = image
      ? `<img class="guide-nav-card__image-img" alt="" src="assets/images/paths/${encodeURIComponent(image)}">`
      : `<svg class="guide-nav-card__image-glyph" width="18" height="20" aria-hidden="true"><use href="#icon-guide"/></svg>`;
    const dot = dotTone
      ? `<span class="guide-nav-card__dot guide-nav-card__dot--${dotTone}" aria-hidden="true"></span>`
      : '';
    return `
      <div class="guide-nav-card__page">
        <div class="path-disc guide-nav-card__image">${inner}</div>
        <span class="guide-nav-card__page-lines" aria-hidden="true"><i></i><i></i></span>${dot}
      </div>`;
  }

  /**
   * The tile's corner-dot tone for a rail state: the rail's own colour for
   * the actionable inks, and none for gray — a Locked row is at rest as far as
   * this card is concerned, so the unread-style marker would lie.
   * @param {{word: string, color: string}|null} rail - `_guideRailState` result
   * @returns {'blue'|'orange'|'red'|null}
   */
  static _railDotTone(rail) {
    if (!rail || rail.color === 'gray') return null;
    return rail.color;
  }

  /**
   * The individual half's status as the UI should show it RIGHT NOW: the
   * persisted `status`, overridden to GENERATING while this section's own
   * `handleGenerateGuide` call for that definition is in flight. Read by the
   * card and by the open reader alike, so the writing block and the (writing)
   * pill appear the moment the button is pressed rather than after the next
   * fetch.
   * @param {Object} row - Merged guide row
   * @returns {string|null} Effective status, or null when no row exists
   */
  _effectiveIndividualStatus(row) {
    if (this.generatingGuideId === row.definitionId) return 'GENERATING';
    return row.individual?.status || null;
  }

  /**
   * The card's date: the later of the two halves' generation stamps, whichever
   * half it belongs to — the You pill's (writing) qualifier already says which
   * half is being written, so a couple's stamp beside it is not misread. It
   * only RENDERS when the rail is empty (`renderGuideCard`'s either/or); an
   * actionable row hides it behind the rail word.
   * @param {Object} row - Merged guide row
   * @returns {string} Relative date, or '' when neither half has a stamp
   */
  static _guideRowDate(row) {
    const stamps = [row.individual?.generatedAt, row.shared?.generatedAt]
      .filter(Boolean)
      .map(at => new Date(at).getTime())
      .filter(t => !Number.isNaN(t));
    if (stamps.length === 0) return '';
    return formatRelativeDate(new Date(Math.max(...stamps)).toISOString());
  }

  /**
   * Whether a `cards.insights` key is currently lit. The card's "New" rail
   * word reads the SAME badge state the Guides-tab dot and the sidebar
   * indicator do (D3), so the three can never disagree.
   * @param {string} key - Card key (`<definitionId>` or `together:<definitionId>`)
   * @returns {boolean}
   */
  static _guideBadgeLit(key) {
    return Boolean(window.badgeService?.getCardState('insights', key));
  }

  /**
   * The You pill: present whenever the viewer's own half is something the
   * card can speak for — READY, GENERATING, ERROR, or unlocked-but-unwritten
   * (`canGenerateGuide`) — and absent otherwise (no `UserGuide` row and not
   * generatable, STALE, anything else). Always filled; the one qualifier it
   * can carry is (writing) while GENERATING. Error / Generate / Locked / New
   * are actionable or inert-with-a-reason, so they belong to the rail, not the
   * pill. A generate-only and a locked row both carry a bare You pill: each
   * says the document will be made of your reading alone, and the rail says
   * what stands between you and it.
   * @param {Object} row - Merged guide row (`row.individual` may be null)
   * @param {string|null} status - Effective individual status (GENERATING-overridden)
   * @returns {string} HTML string ('' when there is no You pill)
   */
  static _guideYouChip(row, status) {
    const present = status === 'READY' || status === 'GENERATING' || status === 'ERROR'
      || InsightsSection.canGenerateGuide(row.individual)
      || InsightsSection.isLockedGuide(row.individual);
    if (!present) return '';
    return renderPartChip({ part: GUIDE_PART_YOU, qualifier: status === 'GENERATING' ? 'writing' : '' });
  }

  /**
   * The Together pill: present whenever the row has a shared half, always
   * filled, with the descriptive qualifier `sharedGuideCardQualifier` resolves
   * — (writing) / (waiting) / (hidden), or none when the couple's reading is
   * readable or the state is the rail's ("Needs review"). Resolves through
   * that explicit map only: nothing here takes or renders a partner name, and
   * plain waiting and floored-out waiting render byte-identically.
   * @param {Object|null} shared - Shared-guide manifest item, if any
   * @returns {string} HTML string ('' when the card has no Together half)
   */
  static _togetherChip(shared) {
    return shared ? renderPartChip({ part: GUIDE_PART_TOGETHER, qualifier: sharedGuideCardQualifier(shared) }) : '';
  }

  /**
   * The rail: the ONE actionable word the card's meta cell shows in place of
   * the date, or null for a row at rest. An ordered chain, first match wins:
   *   1. Error        (red)    — your reading couldn't be written; the card
   *                              stays clickable and retries.
   *   2. Needs review (orange) — the couple's reading waits on YOUR confirm.
   *   3. Generate     (blue)   — unlocked but never written; the reader's
   *                              "Write my guide" button is the affordance
   *                              (`canGenerateGuide`).
   *   4. New          (blue)   — either half is written and unread (badge
   *                              state); a You/Together tie is unobservable.
   *   5. Locked       (gray)   — your half is visible but still behind its
   *                              discovery path (`isLockedGuide`), and nothing
   *                              above claimed the slot. Gray on purpose: the
   *                              rail's blue / orange / red mean "needs you
   *                              HERE", and a locked guide needs you somewhere
   *                              else. Gray is this app's inert ink (Archived
   *                              uses it), so "anything coloured in the right
   *                              column needs you" still holds — which is why
   *                              an unread couple's half outranks it — and the
   *                              tile lights no dot for it.
   * The word does not say which half it refers to — the card is a summary and
   * the detail page carries the rest. Total: never undefined, never throws,
   * `row.individual` may be null (Together-only rows). There is no
   * shared-half error state — `'error'` exists only on the individual half.
   * @param {Object} row - Merged guide row
   * @param {string|null} status - Effective individual status (GENERATING-overridden)
   * @returns {{word: string, color: ('red'|'orange'|'blue'|'gray')}|null}
   */
  static _guideRailState(row, status) {
    if (status === 'ERROR') return { word: 'Error', color: 'red' };
    if (row.shared?.viewerState === 'pending-review') return { word: 'Needs review', color: 'orange' };
    // Both status-null words read the EFFECTIVE status: an in-flight generate
    // has already turned this row into (writing), and (writing) has no rail.
    const unwritten = !status;
    if (unwritten && InsightsSection.canGenerateGuide(row.individual)) return { word: 'Generate', color: 'blue' };
    const unread = (status === 'READY' && InsightsSection._guideBadgeLit(row.definitionId))
      || (row.shared?.status === 'ready' && row.shared?.viewerState === 'sealed'
        && InsightsSection._guideBadgeLit(`together:${row.definitionId}`));
    if (unread) return { word: 'New', color: 'blue' };
    if (unwritten && InsightsSection.isLockedGuide(row.individual)) return { word: 'Locked', color: 'gray' };
    return null;
  }

  /**
   * The card's part pills — the same component the reader's masthead carries
   * — under the tagline: You first, then Together, each present only when the
   * guide has that half to speak for (`_guideYouChip`, `_togetherChip`), so a
   * card always says what the document is made of. Solo and coupled cards
   * render through this one path: a solo card is a single You pill. Anything
   * actionable is the rail's (`_guideRailState`), never a pill's.
   * @param {Object} row - Merged guide row
   * @param {string|null} status - Effective individual status (GENERATING-overridden)
   * @returns {string} HTML string ('' when there is no pill to show)
   */
  renderGuideStatusRow(row, status) {
    const chips = [InsightsSection._guideYouChip(row, status), InsightsSection._togetherChip(row.shared)].filter(Boolean);
    return chips.length ? `<div class="guide-nav-card__parts">${chips.join('')}</div>` : '';
  }

  /**
   * Whether a guide is unlocked but has never been written — the state where
   * tapping the card should START generation.
   *
   * `status` is null exactly when no `UserGuide` row exists (see
   * `getAvailableGuides`), so this is "eligible, nothing generated yet". It
   * mirrors the backend gate (`guideEligibilityService.assertCanGenerate`
   * requires BOTH `eligible` and a resolved `generationMode`) rather than
   * checking `isEligible` alone — the two are equivalent today, but a card that
   * offers a generation the server would refuse is a worse failure than one
   * that stays quiet.
   *
   * Cards in this state render with the blue GENERATE label (`applyGuidesFilters`
   * lets them through on exactly this predicate) and carry no kebab — with no
   * `UserGuide` row persisted, star/archive/delete would be zero-row no-ops
   * that only mutate local state.
   *
   * @param {Object} guide - Guide availability summary row
   * @returns {boolean} True when tapping should generate
   */
  static canGenerateGuide(guide) {
    return Boolean(guide) && !guide.status && Boolean(guide.isEligible) && Boolean(guide.generationMode);
  }

  /**
   * Whether a guide is visible but still locked behind its discovery path —
   * no `UserGuide` row and not eligible. "Locked" means path progress, not
   * premium: the availability row's `lockReason` says what unlocks it, and the
   * reader's locked plate shows that line with a CTA onto the path
   * (`handleContinueGuidePath`). Visibility is not re-checked here because
   * `getAvailableGuides` never returns a non-visible definition at all.
   *
   * @param {Object} guide - Guide availability summary row
   * @returns {boolean} True when the guide is listed but not yet unlockable
   */
  static isLockedGuide(guide) {
    return Boolean(guide) && !guide.status && !guide.isEligible;
  }

  /**
   * Render the hover-revealed kebab options button + its dropdown menu for a
   * guide nav card — Star/Unstar and Archive/Unarchive. Wired identically to
   * the guide-detail panel's own header actions (`_buildMergedGuidePanelPayload`)
   * so the two surfaces can't drift.
   *
   * There is no Delete on any guide surface (Archive is the whole put-it-away
   * vocabulary), and deliberately NO Discuss item: there is no whole-guide
   * discuss entry point anywhere — per-atom Discuss lives inside the guide
   * reader (see 20-guides-and-path-eligibility.md).
   *
   * Star appears only when a `UserGuide` row exists, because star acts on that
   * row: a Together-only card gets Archive alone. A generate-only card (no row
   * on either side) gets no kebab at all — star/archive would be zero-row
   * server no-ops that still flip local state.
   * @param {Object} row - Merged guide row
   * @returns {string} HTML string ('' when there is nothing to act on)
   */
  renderGuideRowOptions(row) {
    const hasOwnRow = InsightsSection._hasUserGuideRow(row.individual);
    if (!hasOwnRow && !row.shared) return '';

    const items = [];
    if (hasOwnRow) {
      const starred = Boolean(row.individual.starred);
      items.push({ action: 'row-star', label: starred ? 'Unstar' : 'Star', icon: starred ? 'star' : 'star-outline' });
    }
    items.push({ action: 'row-archive', label: row.archived ? 'Unarchive' : 'Archive' });
    return renderRowKebab({ items, dataAttrs: { 'data-guide-id': row.definitionId }, ariaLabel: 'Guide options' });
  }

  /**
   * Render patterns tab content
   * @returns {string} HTML string
   */
  renderPatternsContent() {
    if (this.patternsLoading) {
      return `
        <div class="understand-tab-content" data-tab-content="patterns">
          <div class="patterns-loading">
            <div class="loading-spinner"></div>
            <p>Loading patterns...</p>
          </div>
        </div>
      `;
    }

    if (this.patternsError) {
      return `
        <div class="understand-tab-content" data-tab-content="patterns">
          <div class="patterns-error">
            <p>Unable to load patterns. Please try again.</p>
            <button class="btn btn--secondary" data-action="retry-patterns">Retry</button>
          </div>
        </div>
      `;
    }

    return `
      <div class="understand-tab-content" data-tab-content="patterns">
        <div data-filter-bar-mount="patterns" data-sticky-slice></div>
        <div class="session-cards-container">
          ${this._renderPatternsBody()}
        </div>
      </div>
    `;
  }

  /**
   * Pattern cards, or the empty state when none are visible. Shared by the full
   * tab render and `updatePatternsList()`. This tab's filter bar carries no
   * filters and no sort control (the list is always recency-ordered), so the
   * list can never be filtered to zero — no filtered variant.
   * @returns {string} HTML string
   */
  _renderPatternsBody() {
    if (this.filteredPatterns.length === 0) {
      return renderEmptyState({
        title: 'No patterns yet',
        description: "Patterns are recurring themes that emerge across your sessions. Keep having conversations and they'll appear here."
      });
    }
    const activeCode = this._activePatternCode();
    return this.filteredPatterns.map(pattern => this.renderPatternCard(pattern, activeCode)).join('');
  }

  /**
   * Render a single pattern nav card (clickable, opens detail panel)
   * @param {Object} pattern - Pattern data
   * @param {string|null} [activeCode] - Pattern code currently shown in the right panel
   * @returns {string} HTML string
   */
  renderPatternCard(pattern, activeCode = null) {
    const isOpenInPanel = Boolean(activeCode) && pattern.patternCode === activeCode;
    const activeCls = isOpenInPanel ? ' session-card--active' : '';
    const isActive = this._isPatternActive(pattern);
    const dotState = isActive ? 'active' : 'dormant';
    const dormantCls = isActive ? '' : ' pattern-row--dormant';
    const count = pattern.instanceCount ?? 0;
    const seenText = formatTimeAgo(pattern.lastSeenAt, { granularity: 'day' });
    const dateHtml = seenText
      ? `<span class="pattern-row__date">seen ${this.escapeHtml(seenText)}</span>`
      : '';
    return `
      <div class="card session-card pattern-row session-card--iconed${dormantCls}${activeCls}"
           data-pattern-code="${this.escapeHtml(pattern.patternCode)}"
           role="button"
           tabindex="0"
           aria-expanded="${isOpenInPanel}">
        <span class="session-card__slot">
          <span class="pattern-row__dot pattern-row__dot--${dotState}"
                role="img"
                aria-label="${isActive ? 'Active pattern' : 'Dormant pattern'}"></span>
        </span>
        <div class="session-card__content">
          <div class="session-card__title-row">
            <h4 class="session-card__title session-card__title--light">${this.escapeHtml(pattern.name)}</h4>
            <span class="note-row__count"
                  aria-label="${count} observation${count !== 1 ? 's' : ''}">${count}</span>
          </div>
        </div>
        <span class="pattern-row__meta">
          ${dateHtml}
          <span class="session-card__chevron pattern-row__chevron" aria-hidden="true">
            <svg width="19" height="19" viewBox="0 0 16 16">
              <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
          </span>
        </span>
      </div>
    `;
  }

  /**
   * A pattern is "active" if last observed within ACTIVE_WINDOW_DAYS.
   * Missing/invalid lastSeenAt → dormant. Boundary is inclusive (exactly 20d = active).
   * @param {Object} pattern - Pattern data
   * @returns {boolean}
   */
  _isPatternActive(pattern) {
    const ts = pattern?.lastSeenAt ? new Date(pattern.lastSeenAt).getTime() : NaN;
    if (Number.isNaN(ts)) return false;
    return (Date.now() - ts) <= InsightsSection.ACTIVE_WINDOW_DAYS * 86400000;
  }

  /**
   * Render the pattern detail panel content through the shared renderer
   * (`patternDrillHelper.renderPatternDetailBody`), from this section's
   * selected pattern and its remembered instances-zone state.
   * @returns {string} HTML string
   */
  renderPatternDetailPanel() {
    return renderPatternDetailBody(this.selectedPatternData, {
      instancesExpanded: this._patternInstancesExpanded
    });
  }

  /**
   * Render the education panel content
   * @returns {string} HTML string
   */
  renderEducationPanel() {
    if (this.educationLoading) {
      return `
        <div class="pattern-education">
          <div class="pattern-education__card">
            <div class="pattern-education__loading">
              <div class="loading-spinner"></div>
              <p>Loading...</p>
            </div>
          </div>
        </div>
      `;
    }

    if (!this.educationPatternData) {
      return `
        <div class="pattern-education">
          <div class="pattern-education__card">
            <div class="pattern-education__empty">
              <p>Pattern information not available.</p>
            </div>
          </div>
        </div>
      `;
    }

    const data = this.educationPatternData;

    // Build sections based on available data
    const sections = [];

    // What It Is section
    if (data.whatItIs) {
      sections.push(`
        <div class="pattern-education__section">
          <h3 class="pattern-education__section-title">Understanding this pattern</h3>
          <div class="pattern-education__section-content">
            ${this.formatEducationText(data.whatItIs)}
          </div>
        </div>
      `);
    }

    // How It Shows Up section
    if (data.howItShowsUp && data.howItShowsUp.length > 0) {
      sections.push(`
        <div class="pattern-education__section">
          <h3 class="pattern-education__section-title">Signs you might notice</h3>
          <ul class="pattern-education__list">
            ${data.howItShowsUp.map(item => `<li>${this.escapeHtml(item)}</li>`).join('')}
          </ul>
        </div>
      `);
    }

    // Why It Happens section
    if (data.whyItHappens) {
      sections.push(`
        <div class="pattern-education__section">
          <h3 class="pattern-education__section-title">Why this happens</h3>
          <div class="pattern-education__section-content">
            ${this.formatEducationText(data.whyItHappens)}
          </div>
        </div>
      `);
    }

    // Path Forward section
    if (data.pathForward) {
      sections.push(`
        <div class="pattern-education__section">
          <h3 class="pattern-education__section-title">Moving forward</h3>
          <div class="pattern-education__section-content">
            ${this.formatEducationText(data.pathForward)}
          </div>
        </div>
      `);
    }

    // Fallback if no educational content yet
    if (sections.length === 0) {
      sections.push(`
        <div class="pattern-education__section">
          <h3 class="pattern-education__section-title">About this pattern</h3>
          <div class="pattern-education__section-content">
            <p>${this.escapeHtml(data.userDescription || data.description || 'Educational content coming soon.')}</p>
          </div>
        </div>
      `);
    }

    return `
      <div class="pattern-education">
        <div class="pattern-education__card">
          <div class="pattern-education__title-block">
            <h2 class="pattern-education__title">${this.escapeHtml(data.name)}</h2>
            ${data.tagline ? `<p class="pattern-education__tagline">${this.escapeHtml(data.tagline)}</p>` : ''}
          </div>

          ${sections.join('')}
        </div>
      </div>
    `;
  }

  /**
   * Format education text - converts paragraphs separated by newlines
   * @param {string} text - Raw text with potential newlines
   * @returns {string} HTML formatted paragraphs
   */
  formatEducationText(text) {
    if (!text) return '';
    // Split by double newlines for paragraphs
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim());
    return paragraphs.map(p => `<p>${this.escapeHtml(p.trim())}</p>`).join('');
  }

  /**
   * Render the assessments (assessment results) tab content — one nav card per
   * completed clinical framework. Reuses the shared `.session-card` markup.
   * @returns {string} HTML string
   */
  renderFrameworksContent() {
    if (this.frameworksLoading) {
      return `
        <div class="understand-tab-content" data-tab-content="assessments">
          <div class="patterns-loading">
            <div class="loading-spinner"></div>
            <p>Loading assessment results...</p>
          </div>
        </div>
      `;
    }

    if (this.frameworksError) {
      return `
        <div class="understand-tab-content" data-tab-content="assessments">
          <div class="patterns-error">
            <p>Unable to load assessment results. Please try again.</p>
            <button class="btn btn--secondary" data-action="retry-frameworks">Retry</button>
          </div>
        </div>
      `;
    }

    return `
      <div class="understand-tab-content" data-tab-content="assessments">
        <div data-filter-bar-mount="frameworks" data-sticky-slice></div>
        <div class="session-cards-container">
          ${this._renderFrameworksBody()}
        </div>
      </div>
    `;
  }

  /**
   * Assessment rows, or the empty state when none are visible. Shared by the
   * full tab render and `updateFrameworksList()`. This tab's filter bar offers
   * sort only, so the list can never be filtered to zero — no filtered variant.
   * @returns {string} HTML string
   */
  _renderFrameworksBody() {
    if (this.filteredFrameworks.length === 0) {
      return renderEmptyState({
        title: 'No assessment results yet',
        description: 'Complete assessments in Discovery to see your results here — attachment style, personality profile, and more.'
      });
    }
    const activeId = this._activeFrameworkId();
    return this.filteredFrameworks.map(fw => this.renderFrameworkCard(fw, activeId)).join('');
  }

  /**
   * Render a single assessment-result row (clickable, opens detail panel).
   * Single line, title only; the completion date on the right swaps to the
   * shared chevron on hover — same interaction as the /notes rows.
   * @param {Object} fw - { questionnaireId, frameworkCode, title, completedAt, classification, insight }
   * @param {string|null} [activeId] - Questionnaire id currently shown in the right panel
   * @returns {string} HTML string
   */
  renderFrameworkCard(fw, activeId = null) {
    const isOpenInPanel = Boolean(activeId) && fw.questionnaireId === activeId;
    const activeCls = isOpenInPanel ? ' session-card--active' : '';
    const dateHtml = fw.completedAt
      ? `<span class="framework-row__date">${this.formatFrameworkDate(fw.completedAt)}</span>`
      : '';
    return `
      <div class="card session-card framework-row session-card--iconed${activeCls}"
           data-framework-id="${this.escapeHtml(fw.questionnaireId)}"
           role="button"
           tabindex="0"
           aria-expanded="${isOpenInPanel}">
        <span class="session-card__slot">
          <span class="session-card__type-icon" aria-hidden="true">
            <svg width="21" height="21"><use href="#icon-assessment"/></svg>
          </span>
        </span>
        <div class="session-card__content">
          <div class="session-card__title-row">
            <h4 class="session-card__title session-card__title--light">${this.escapeHtml(fw.title)}</h4>
          </div>
        </div>
        <span class="framework-row__meta">
          ${dateHtml}
          <span class="session-card__chevron framework-row__chevron" aria-hidden="true">
            <svg width="19" height="19" viewBox="0 0 16 16">
              <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
          </span>
        </span>
      </div>
    `;
  }

  /**
   * Build the assessment-result detail panel content — the exact Discovery
   * "View full results" view (structured score-bar detail via
   * `buildStructuredDetailPanel`, same as the chat insight block and the
   * topic drill), or the persisted prose for frameworks without a composer.
   * @param {Object} fw - One framework result (carries `insight` from the API)
   * @returns {HTMLElement}
   */
  _buildFrameworkDetailContent(fw) {
    const insight = fw.insight;
    const structured = insight?.format === 'structured' && insight.detail;
    return buildAssessmentDetailContent({
      // `completedAt` comes from the framework-results row, not the composed
      // payload — this is the only host that has it.
      detail: structured ? { ...insight.detail, completedAt: fw.completedAt } : null,
      markdown: insight?.markdown || ''
    });
  }

  /**
   * Format an assessment completion date (absolute — results can be old).
   * @param {string} dateStr - ISO date string
   * @returns {string} Formatted date (e.g., "Feb 5, 2026")
   */
  formatFrameworkDate(dateStr) {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  static ORIENTATION_MESSAGES = {
    topics: 'Topics organize what we\'re learning about you across the areas of your life. Open one to see a summary of what\'s emerged so far.',
    patterns: 'Patterns emerge when similar observations appear across multiple sessions. They reveal recurring themes and deeper dynamics in your relationship.',
    guides: 'Guides are curated perspectives on topics relevant to your journey, drawing on what we\'ve learned together to offer tailored educational content.',
    assessments: 'Your assessment results live here — what structured questionnaires like the attachment-style assessment revealed, in one place.',
  };

  // Moved tabs keep their pre-move localStorage keys so banners users
  // already dismissed on /library don't reappear here. `topics` reuses the
  // exact key its (now-deleted) standalone page used, since it's the one tab
  // migrating with real prior dismissal history.
  static BANNER_KEYS = {
    topics: 'insights-topics',
    patterns: 'insights-patterns',
    guides: 'insights-guides',
    assessments: 'library-assessments',
  };

  /** No `topics` row — that page sub-tab has no info tab; the `|| DEFAULT_TAB`
   * fallback at the call site routes it to Guides. */
  static TAB_TO_INFO_TAB = {
    patterns: 'patterns',
    guides: 'guides',
    assessments: 'assessments',
  };

  _showOrientationBanner(tab) {
    if (this.orientationBanner) {
      this.orientationBanner.destroy();
      this.orientationBanner = null;
    }
    const message = InsightsSection.ORIENTATION_MESSAGES[tab];
    if (!message) return;
    const contentContainer = this.container.querySelector('.understand-section__content');
    if (!contentContainer) return;
    const infoTab = InsightsSection.TAB_TO_INFO_TAB[tab] || InsightsSection.DEFAULT_TAB;
    this.orientationBanner = new OrientationBanner({
      container: contentContainer,
      key: InsightsSection.BANNER_KEYS[tab],
      message,
      linkText: 'Learn more',
      onLinkClick: () => this.showPageInfo(infoTab),
    });
    this.orientationBanner.show();
  }

  /** Three tabs, deliberately not four: the page's Topics sub-tab carries no
   * info tab (owner decision) — a topics activeTab falls back to the first
   * key inside buildPageInfoPanel. */
  static PAGE_INFO_TABS = {
    patterns: {
      label: 'Patterns',
      content: `
        <h4 class="page-info-detail__subtitle">Recurring Themes</h4>
        <p class="page-info-detail__text">
          Patterns emerge when similar observations appear across multiple
          sessions — recurring themes, persistent dynamics, or evolving
          narratives in your relationship, gathered here in one place.
        </p>
        ${renderPageInfoFaq([
          {
            q: 'Where do patterns come from?',
            a: `From your sessions. When similar observations keep appearing
              across conversations, they're drawn together into a pattern.`,
          },
          {
            q: 'Why do patterns matter?',
            a: `They turn isolated moments into a coherent story — and
              highlight the areas where change is most possible.`,
          },
          {
            q: 'Do patterns change over time?',
            a: `They can. Patterns reflect what's appearing across your
              sessions, so they evolve as your story does.`,
          },
        ])}
      `,
    },
    guides: {
      label: 'Guides',
      content: `
        <h4 class="page-info-detail__subtitle">Tailored Perspectives</h4>
        <p class="page-info-detail__text">
          Guides are educational content tailored to your journey. When
          Discovery assessments complete for a topic, a guide is generated
          that turns what we've learned into practical, relevant insight.
        </p>
        ${renderPageInfoFaq([
          {
            q: 'When does a guide appear?',
            a: `When you've completed the Discovery work it draws on —
              finishing a topic's assessments is what unlocks it.`,
          },
          {
            q: 'How is this different from generic advice?',
            a: `Each guide is written from your own assessments and
              conversations, so it reflects your relationship's actual
              dynamics and themes.`,
          },
        ])}
      `,
    },
    assessments: {
      label: 'Assessments',
      content: `
        <h4 class="page-info-detail__subtitle">Your Assessment Results</h4>
        <p class="page-info-detail__text">
          When you complete a structured assessment in Discovery — like the
          attachment-style or personality questionnaire — its results are kept
          here so you can revisit them any time.
        </p>
        ${renderPageInfoFaq([
          {
            q: 'What do the results show?',
            a: `What the assessment found and what it means for your
              relationship, drawn from clinically validated instruments.`,
          },
          {
            q: 'Where do I take assessments?',
            a: `In Discovery — they're part of its topics and paths. Results
              land here when you finish.`,
          },
        ])}
      `,
    },
  };

  /**
   * Attach persistent event listeners (page-level UI that survives tab changes).
   * Per-tab listeners are attached separately by `_attachTopicsTabListeners`,
   * `_attachPatternsTabListeners`, `_attachGuidesTabListeners`, and
   * `_attachFrameworksTabListeners` after each tab content render.
   */
  attachEventListeners() {
    Tooltip.setupAll(this.container, '[data-tooltip]', { position: 'below' });

    const navItems = this.container.querySelectorAll(NAV_ITEM_SELECTOR);
    navItems.forEach(item => {
      item.addEventListener('click', () => {
        const tab = item.dataset.tab;
        if (tab && tab !== this.currentTab) {
          this.handleTabChange(tab);
        }
      });
    });
  }

  /**
   * Attach listeners for the topics list (hidden tab). Delegates card + action
   * clicks via the `[data-tab-content="topics"]` wrapper, which survives list
   * re-renders (filter/sort changes only rebuild the inner `.session-cards-container`).
   */
  _attachTopicsTabListeners() {
    this._mountTopicsFilterBar();

    const wrapper = this.container.querySelector('[data-tab-content="topics"]');
    if (!wrapper) return;

    wrapper.addEventListener('click', (e) => {
      const topicCard = e.target.closest('.session-card[data-topic-id]');
      if (topicCard) {
        this.handleTopicCardClick(topicCard.dataset.topicId);
        return;
      }
      const target = e.target.closest('[data-action]');
      if (!target) return;
      if (target.dataset.action === 'retry-topics') {
        this.loadTopicsData();
      }
      if (target.dataset.action === 'clear-filters') {
        this.topicsFilterBar?.reset();
      }
    });
  }

  /**
   * Mount the shared ResultsFilterBar for the topics list — count + Core/Life
   * filter + explored sort. (A "Recently updated" sort will be added once the
   * topic-summary table provides a per-topic timestamp.)
   */
  _mountTopicsFilterBar() {
    const mount = this.container.querySelector('[data-filter-bar-mount="topics"]');
    if (!mount) return;
    if (this.topicsFilterBar) {
      this.topicsFilterBar.destroy();
      this.topicsFilterBar = null;
    }
    this.topicsFilterBar = new ResultsFilterBar({
      container: mount,
      itemLabel: { singular: 'Topic', plural: 'Topics' },
      fixedItemLabel: { singular: 'Topic', plural: 'Topics' },
      count: this.filteredTopics.length,
      filters: [
        {
          id: 'scope',
          label: 'Show',
          defaultValue: this.topicScope,
          options: [
            { value: 'all', label: 'All' },
            { value: 'core', label: 'Core' },
            { value: 'life', label: 'Life' }
          ]
        }
      ],
      sort: {
        defaultValue: this.topicSortOrder,
        options: [
          { value: 'default', label: 'Suggested' },
          { value: 'most', label: 'Most explored' },
          { value: 'least', label: 'Least explored' }
        ]
      },
      onChange: (state) => this._handleTopicFilterChange(state)
    });
  }

  /**
   * Handle a filter/sort change from the topics bar.
   * @param {{filters: Object, sort: string}} state
   */
  _handleTopicFilterChange(state) {
    this.topicScope = state.filters.scope;
    this.topicSortOrder = state.sort;
    this.applyTopicFilterSort();
    this.updateTopicsList();
  }

  /**
   * Apply the active Core/Life filter + explored sort into `filteredTopics`.
   * 'default' preserves the API order (taxonomy displayOrder: core then life).
   */
  applyTopicFilterSort() {
    let list = [...this.topics];
    if (this.topicScope === 'core' || this.topicScope === 'life') {
      list = list.filter(t => t.type === this.topicScope);
    }
    if (this.topicSortOrder === 'most') {
      list.sort((a, b) => (b.progress || 0) - (a.progress || 0));
    } else if (this.topicSortOrder === 'least') {
      list.sort((a, b) => (a.progress || 0) - (b.progress || 0));
    }
    this.filteredTopics = list;
  }

  /**
   * Attach listeners for the patterns tab. Mounts the filter bar and delegates
   * card + action clicks via the `[data-tab-content="patterns"]` wrapper, which
   * survives within-tab list re-renders (filter/sort changes only rebuild the
   * inner `.session-cards-container`).
   */
  _attachPatternsTabListeners() {
    this._mountPatternFilterBar();

    const wrapper = this.container.querySelector('[data-tab-content="patterns"]');
    if (!wrapper) return;

    wrapper.addEventListener('click', (e) => {
      const patternCard = e.target.closest('.session-card[data-pattern-code]');
      if (patternCard) {
        this.handlePatternCardClick(patternCard.dataset.patternCode);
        return;
      }
      const target = e.target.closest('[data-action]');
      if (!target) return;
      if (target.dataset.action === 'retry-patterns') {
        this.loadPatternsData();
      }
    });
  }

  /**
   * Attach listeners for the guides tab. Same stable-wrapper pattern as
   * `_attachPatternsTabListeners`. The card's star is a static marker (not a
   * button); Star/Unstar and Archive/Unarchive live in the row's own kebab
   * (`renderGuideRowOptions`) and mirror the detail panel's header actions.
   * Row-kebab clicks are handled first (and return early) so they never also
   * open the detail panel.
   *
   * One card selector, because there is one card: a definition's two halves
   * share a row keyed by `data-guide-id`, and a Together-only card opens
   * through the same dispatch as any other.
   */
  _attachGuidesTabListeners() {
    this._mountGuidesFilterBar();

    const wrapper = this.container.querySelector('[data-tab-content="guides"]');
    if (!wrapper) return;

    wrapper.addEventListener('click', (e) => {
      const rowAction = e.target.closest('[data-action^="row-"]');
      if (rowAction) {
        this._handleGuideRowAction(e, rowAction);
        return;
      }
      const guideCard = e.target.closest('.guide-nav-card[data-guide-id]');
      if (guideCard) {
        this.handleGuideCardClick(guideCard.dataset.guideId);
        return;
      }
      const target = e.target.closest('[data-action]');
      if (!target) return;
      if (target.dataset.action === 'retry-guides') {
        this.loadGuidesData();
      }
      // Same action as the filter menu's "Reset filters" row — one code path.
      if (target.dataset.action === 'clear-filters') {
        this.guidesFilterBar?.reset();
      }
    });

    // Keyboard parity: the cards carry role="button" + tabindex="0", so
    // Enter/Space must activate them the same way a click does.
    wrapper.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const guideCard = e.target.closest('.guide-nav-card[data-guide-id]');
      if (!guideCard) return;
      e.preventDefault();
      this.handleGuideCardClick(guideCard.dataset.guideId);
    });

    // Right-click a guide card to open its kebab menu (same as the sidebar).
    wrapper.addEventListener('contextmenu', (e) => this._guideRowMenu.handleContextMenu(e));
  }

  /**
   * Delegated handler for a guide row's kebab (`row-options`) and its menu
   * items (`row-star`/`row-archive`). Star routes through `_runGuideBulk` with
   * a single-id array; Archive routes through `_setGuideRowArchived`, which
   * acts on every present half of the card (D8) — both the exact same paths
   * the detail panel's Options dropdown uses, so the two surfaces can't drift.
   * @param {MouseEvent} event
   * @param {HTMLElement} target - The clicked `[data-action]` element
   */
  _handleGuideRowAction(event, target) {
    event.stopPropagation();
    const action = target.getAttribute('data-action');
    const guideId = target.getAttribute('data-guide-id');

    switch (action) {
      case 'row-options':
        this._guideRowMenu.toggle(target);
        break;
      case 'row-star':
        this._guideRowMenu.close();
        if (guideId) this._runGuideBulk('star', [guideId]);
        break;
      case 'row-archive': {
        this._guideRowMenu.close();
        const row = guideId ? this._guideRow(guideId) : null;
        if (row) this._setGuideRowArchived(guideId, !row.archived);
        break;
      }
      default:
        break;
    }
  }

  /**
   * Attach listeners for the assessments (assessment results) tab. Same
   * stable-wrapper delegation as `_attachPatternsTabListeners`.
   */
  _attachFrameworksTabListeners() {
    this._mountFrameworksFilterBar();

    const wrapper = this.container.querySelector('[data-tab-content="assessments"]');
    if (!wrapper) return;

    wrapper.addEventListener('click', (e) => {
      const card = e.target.closest('.session-card[data-framework-id]');
      if (card) {
        this.handleFrameworkCardClick(card.dataset.frameworkId);
        return;
      }
      const target = e.target.closest('[data-action]');
      if (!target) return;
      if (target.dataset.action === 'retry-frameworks') {
        this.loadFrameworksData();
      }
    });
  }

  /**
   * Handle tab change.
   *
   * Pushes the new tab to the URL via `page.replace` (filter-style change,
   * no new history entry). The route handler re-fires applyURLParams which
   * calls back into this method with `skipUrlPush: true` to avoid a loop.
   *
   * @param {string} tab - New tab identifier
   * @param {Object} [options]
   * @param {boolean} [options.skipUrlPush=false] - Set when called from
   *   applyURLParams to avoid re-pushing the URL we just read from.
   */
  handleTabChange(tab, { skipUrlPush = false } = {}) {
    if (!skipUrlPush) {
      // Push the tab change through the URL. Drop any open detail param —
      // a tab change implicitly closes the panel and the close listener
      // will scrub stale params, but doing it here also keeps the URL
      // clean during the route-handler round-trip.
      const url = new URL(window.location.href);
      url.searchParams.set('tab', tab);
      url.searchParams.delete('pattern');
      url.searchParams.delete('guide');
      url.searchParams.delete('sharedGuide');
      url.searchParams.delete('framework');
      url.searchParams.delete('topic');
      page.replace(url.pathname + url.search);
      return;
    }

    if (this.currentTab === 'topics' && tab !== 'topics' && this.topicsFilterBar) {
      this.topicsFilterBar.destroy();
      this.topicsFilterBar = null;
    }
    if (this.currentTab === 'patterns' && tab !== 'patterns' && this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    if (this.currentTab === 'guides' && tab !== 'guides' && this.guidesFilterBar) {
      this.guidesFilterBar.destroy();
      this.guidesFilterBar = null;
    }
    if (this.currentTab === 'assessments' && tab !== 'assessments' && this.frameworksFilterBar) {
      this.frameworksFilterBar.destroy();
      this.frameworksFilterBar = null;
    }
    this.currentTab = tab;

    const navItems = this.container.querySelectorAll(NAV_ITEM_SELECTOR);
    navItems.forEach(item => {
      const isActive = item.dataset.tab === tab;
      item.classList.toggle(NAV_ACTIVE_CLASS, isActive);
      item.setAttribute('aria-selected', isActive.toString());
    });

    const contentContainer = this.container.querySelector('.understand-section__content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderTabContent();
    }
    this._showOrientationBanner(tab);

    if (tab === 'topics') {
      this._attachTopicsTabListeners();
      if (this.topics.length === 0 && !this.topicsLoading) {
        this.loadTopicsData();
      }
    } else if (tab === 'patterns') {
      this._attachPatternsTabListeners();
      if (this.patterns.length === 0 && !this.patternsLoading) {
        this.loadPatternsData();
      }
    } else if (tab === 'guides') {
      this._attachGuidesTabListeners();
      if (this.guides.length === 0 && !this.guidesLoading) {
        this.loadGuidesData();
      }
    } else if (tab === 'assessments') {
      this._attachFrameworksTabListeners();
      if (!this.frameworksLoaded && !this.frameworksLoading) {
        this.loadFrameworksData();
      }
    }
  }

  /**
   * Load the 12 taxonomy topics from discovery progress.
   */
  async loadTopicsData() {
    this.topicsLoading = true;
    this.topicsError = null;
    this.updateTopicsUI();

    try {
      // Progress drives the canonical 12 topics (label/description/type/progress);
      // the summaries endpoint enriches each with its card data (blurb, status,
      // coverage). Fetch in parallel and merge by id — summaries may be empty
      // (table not yet populated), in which case cards degrade to description.
      const [result, summaries] = await Promise.all([
        this.discoveryAPI.getCoreProgress(),
        API.getAllTopicSummaries()
      ]);
      const groups = result?.data?.groups;
      if (!groups) {
        throw new Error('Failed to fetch topics');
      }
      const summaryById = new Map((summaries || []).map(s => [s.topicId, s]));
      this.topics = groups.map(group => {
        const s = summaryById.get(group.id);
        return {
          id: group.id,
          label: group.label,
          description: group.description,
          type: group.type, // 'core' | 'life'
          progress: typeof group.progress === 'number' ? group.progress : 0,
          cardBlurb: s?.cardBlurb ?? null,
          status: s?.status ?? 'none',          // FRESH | STALE | GENERATING | none
          overallCoverage: s?.overallCoverage ?? 'none',
          generatedAt: s?.generatedAt ?? null,
          hasData: Boolean(s?.hasData)
        };
      });
      this.topicsLoading = false;
      this.applyTopicFilterSort();
      this.updateTopicsUI();
      this._resolvePendingDetailFromURL();
    } catch (error) {
      console.error('[InsightsSection] Error loading topics:', error);
      this.topicsLoading = false;
      this.topicsError = error.message;
      this.topics = [];
      this.updateTopicsUI();
    }
  }

  /**
   * Re-render the topics content (used for loading/error/loaded transitions).
   */
  updateTopicsUI() {
    if (this.currentTab !== 'topics') return;

    const contentContainer = this.container.querySelector('.understand-section__content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderTopicsContent();
      this._attachTopicsTabListeners();
    }
  }

  /**
   * Re-render just the topic cards (keeps active-highlight in sync on panel open/close).
   */
  updateTopicsList() {
    const cardsContainer = this.container.querySelector('[data-tab-content="topics"] .session-cards-container');
    if (cardsContainer) {
      cardsContainer.innerHTML = this._renderTopicsBody();
    }
    if (this.topicsFilterBar) {
      this.topicsFilterBar.setCount(this.filteredTopics.length);
    }
  }

  /**
   * Load patterns data from API
   */
  async loadPatternsData() {
    this.patternsLoading = true;
    this.patternsError = null;
    this.updatePatternsUI();

    try {
      const patterns = await API.getPatterns({ status: 'active' });

      if (!patterns) {
        throw new Error('Failed to fetch patterns');
      }

      this.patterns = patterns;
      this.patternsLoading = false;
      this.applyPatternSort();
      this.updatePatternsUI();

      // If the URL is asking for a specific pattern detail that wasn't
      // resolvable when applyURLParams first ran (because patterns hadn't
      // loaded yet), open it now.
      this._resolvePendingDetailFromURL();
    } catch (error) {
      console.error('[InsightsSection] Error loading patterns:', error);
      this.patternsLoading = false;
      this.patternsError = error.message;
      this.filteredPatterns = [];
      this.updatePatternsUI();
    }
  }

  /**
   * Update patterns UI after state change (full re-render, remounts filter bar)
   */
  updatePatternsUI() {
    if (this.currentTab !== 'patterns') return;

    const contentContainer = this.container.querySelector('.understand-section__content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderPatternsContent();
      this._attachPatternsTabListeners();
    }
  }

  /**
   * Update only the cards list (after sort change)
   */
  updatePatternsList() {
    const cardsContainer = this.container.querySelector('[data-tab-content="patterns"] .session-cards-container');
    if (cardsContainer) {
      cardsContainer.innerHTML = this._renderPatternsBody();
    }
    if (this.filterBar) {
      this.filterBar.setCount(this.filteredPatterns.length);
    }
  }

  /**
   * Currently-selected topic id (open in the right panel), or null.
   * @returns {string|null}
   */
  _activeTopicId() {
    return this._panelDetail?.type === 'topic' ? this._panelDetail.id : null;
  }

  /**
   * Currently-selected pattern code (open in the right panel), or null.
   * Sourced from `_panelDetail` so it's set synchronously on click — before
   * the async detail-data fetch resolves.
   * @returns {string|null}
   */
  _activePatternCode() {
    return this._panelDetail?.type === 'pattern' ? this._panelDetail.id : null;
  }

  /**
   * Currently-selected guide id (open in the right panel), or null.
   * @returns {string|null}
   */
  _activeGuideId() {
    return this._panelDetail?.type === 'guide' ? this._panelDetail.id : null;
  }

  /**
   * Currently-selected framework questionnaire id (open in the right panel), or null.
   * @returns {string|null}
   */
  _activeFrameworkId() {
    return this._panelDetail?.type === 'framework' ? this._panelDetail.id : null;
  }

  /**
   * Mount the ResultsFilterBar for the patterns tab — count display only
   * (no sort/filter controls; the list is always recency-ordered).
   */
  _mountPatternFilterBar() {
    const mount = this.container.querySelector('[data-filter-bar-mount="patterns"]');
    if (!mount) return;
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    this.filterBar = new ResultsFilterBar({
      container: mount,
      itemLabel: { singular: 'Pattern', plural: 'Patterns' },
      count: this.filteredPatterns.length
    });
  }

  /**
   * Order filteredPatterns by recency (most-recently-seen first). No user-facing
   * sort/filter control — the list is always recency-ordered.
   */
  applyPatternSort() {
    this.filteredPatterns = [...this.patterns].sort(
      (a, b) => (Date.parse(b.lastSeenAt) || 0) - (Date.parse(a.lastSeenAt) || 0)
    );
  }

  /**
   * Handle pattern card click — push URL, then open detail panel.
   *
   * @param {string} patternCode - Pattern code (slug)
   * @param {Object} [options]
   * @param {boolean} [options.skipUrlPush=false] - Set when called from
   *   applyURLParams (URL is already correct).
   */
  async handlePatternCardClick(patternCode, { skipUrlPush = false } = {}) {
    if (this._panelOpen && this._panelDetail?.type === 'pattern' && this._panelDetail.id === patternCode) {
      // Toggle only on the user-click path; a route-driven call (skipUrlPush)
      // must stay a no-op or a URL replay would slam the panel shut.
      if (!skipUrlPush) this.eventBus.emit('right-panel:close');
      return;
    }

    const pattern = this.patterns.find(p => p.patternCode === patternCode);
    if (!pattern) return;

    if (!skipUrlPush) {
      // Push when panel closed; replace when panel open (cross-fade swap).
      const url = new URL(window.location.href);
      url.searchParams.set('pattern', patternCode);
      url.searchParams.delete('guide');
      url.searchParams.delete('sharedGuide');
      url.searchParams.delete('framework');
      url.searchParams.delete('topic');
      const target = url.pathname + url.search;
      if (this._panelOpen) {
        page.replace(target);
      } else {
        page(target);
      }
      return;
    }

    this.selectedGuide = null;
    this.selectedPatternData = pattern;
    this._panelDetail = { type: 'pattern', id: patternCode };
    this.updatePatternsList();

    const shouldLoadPatternDetail = !pattern.instances;
    this._patternInstancesExpanded = true;
    const content = document.createElement('div');
    content.innerHTML = this.renderPatternDetailPanel();
    this._detailContentEl = content;

    // Zone headers toggle their body via the shared primitive — delegated on
    // `content`, so it survives the async instance hydration below.
    wireDisclosures(content, {
      onToggle: (key, expanded) => {
        if (key === PATTERN_INSTANCES_ZONE) this._patternInstancesExpanded = expanded;
      }
    });

    content.addEventListener('click', (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;
      e.preventDefault();
      const action = target.dataset.action;
      if (action === 'learn-more') {
        this.handleLearnMore(target.dataset.pattern);
      } else if (action === 'view-session') {
        openSessionInPanel(target.dataset.sessionNumber);
      }
    });

    this._panelOpen = true;
    this.eventBus.emit('right-panel:open', {
      title: '',
      titleAlign: 'left',
      primaryButton: {
        label: 'Discuss pattern',
        onClick: () => this.handleDiscussPattern(pattern.patternCode)
      },
      content,
      contentType: 'library-pattern'
    });

    if (shouldLoadPatternDetail) {
      this.loadPatternDetail(pattern, content);
    }
  }

  /**
   * Load full pattern detail on demand and refresh the open detail panel.
   * @param {Object} pattern - List-level pattern object to enrich in place
   * @param {HTMLElement} content - Right-panel content element for this open
   */
  async loadPatternDetail(pattern, content) {
    const patternCode = pattern.patternCode;

    try {
      const fullPattern = await API.getPatternByCode(patternCode);
      if (fullPattern) {
        Object.assign(pattern, fullPattern);
      }
    } catch (error) {
      console.error(`[InsightsSection] Error loading instances for ${patternCode}:`, error);
      return;
    }

    if (
      this._panelOpen &&
      this._panelDetail?.type === 'pattern' &&
      this._panelDetail.id === patternCode &&
      this._detailContentEl === content
    ) {
      this._detailContentEl.innerHTML = this.renderPatternDetailPanel();
    }
  }

  /**
   * Clear the routed detail selection while leaving the panel open.
   * Used when a non-routed page-info panel replaces a detail.
   */
  clearActiveDetailSelection() {
    this._detailContentEl = null;
    this.educationPatternCode = null;
    this.educationPatternData = null;
    this.educationLoading = false;
    this.selectedGuide = null;
    this.selectedPatternData = null;
    this._panelDetail = null;
    this._clearTopicSummaryPoll();
    this.updatePatternsList();
    this.updateGuidesList();
    this.updateFrameworksList();
    this.updateTopicsList();

    const url = new URL(window.location.href);
    const hadDetailParam = url.searchParams.has('pattern') || url.searchParams.has('guide') ||
      url.searchParams.has('sharedGuide') || url.searchParams.has('framework') || url.searchParams.has('topic');
    if (hadDetailParam) {
      url.searchParams.delete('pattern');
      url.searchParams.delete('guide');
      url.searchParams.delete('sharedGuide');
      url.searchParams.delete('framework');
      url.searchParams.delete('topic');
      page.replace(url.pathname + (url.search ? url.search : ''));
    }
  }

  /**
   * Handle discuss pattern action — resolves the pattern from this section's
   * list and hands it to the shared `launchPatternDiscussion` (ADVICE
   * `discuss_pattern` navigate, with its own double-click guard).
   * @param {string} patternCode - Pattern code to discuss
   */
  handleDiscussPattern(patternCode) {
    const pattern = this.patterns.find(p => p.patternCode === patternCode);
    if (!pattern) return;
    launchPatternDiscussion(pattern);
  }

  /**
   * Handle learn more action - opens education panel
   * @param {string} patternCode - Pattern code to show education for
   */
  async handleLearnMore(patternCode) {
    if (!patternCode) return;

    this.educationPatternCode = patternCode;
    this.educationLoading = true;
    this.educationPatternData = null;

    const content = document.createElement('div');
    content.innerHTML = this.renderEducationPanel();
    this._detailContentEl = content;

    this.eventBus.emit('right-panel:drill', {
      title: 'Learn about this pattern',
      titleAlign: 'center',
      content,
      contentType: 'library-education'
    });

    try {
      const definition = await API.getPatternDefinition(patternCode);
      if (definition) {
        this.educationPatternData = definition;
      }
    } catch (error) {
      console.error('[InsightsSection] Error loading pattern definition:', error);
    } finally {
      this.educationLoading = false;
      if (this._detailContentEl) {
        this._detailContentEl.innerHTML = this.renderEducationPanel();
      }
    }
  }

  /**
   * Load guides data from API
   */
  async loadGuidesData() {
    this.guidesLoading = true;
    this.guidesError = null;
    this.updateGuidesUI();

    try {
      // Fetched alongside, not after: `API.getSharedGuides()` never throws or
      // returns null (404/flag-off/error all resolve to []), so it can never
      // be the reason this try/catch falls into the own-guides error branch
      // below — own guides render exactly as they did before D3 regardless
      // of whether the shared-guides fetch succeeded (decision F1).
      // Not requested at all without a bootstrap-time coupleId (as
      // BadgeService already does): an uncoupled account would only ever
      // get a 404 back. Read lazily off the global — app.js nulls it on
      // `partner:unlinked`, and there is no live `partner:linked` reveal.
      const coupleId = window.app?.currentUser?.coupleId;
      const [guides, sharedGuides] = await Promise.all([
        API.getAvailableGuides(),
        coupleId ? API.getSharedGuides() : Promise.resolve([])
      ]);

      if (!guides) {
        throw new Error('Failed to fetch guides');
      }

      this.guides = guides;
      this.sharedGuides = Array.isArray(sharedGuides) ? sharedGuides : [];
      this.guidesLoading = false;
      // Both fetches have now settled — set even when either array came back
      // empty (a real zero-guides account is a valid loaded state, not an
      // unloaded one). See the field's own comment for why this exists
      // separately from `this.guides.length > 0`.
      this._guidesTabLoaded = true;
      this.applyGuidesFilters();
      this.updateGuidesUI();

      if (this.sharedGuides.length > 0) this._loadPartnerNameIfNeeded();

      // Re-resolve any URL detail param that wasn't resolvable when
      // applyURLParams first ran (because guides hadn't loaded yet).
      this._resolvePendingDetailFromURL();
    } catch (error) {
      console.error('[InsightsSection] Error loading guides:', error);
      this.guidesLoading = false;
      this.guidesError = error.message;
      this.filteredGuides = [];
      this.updateGuidesUI();
    }
  }

  /**
   * Lazily fetch the couple partner's display name for the merged reader's
   * copy (`buildMergedGuideDetailPayload` / `renderGuideYouPart` take
   * `partnerName`). The /library card never names the partner, so nothing
   * here re-renders the list when the name lands. Cached on `this._partner`
   * for the section's lifetime (unlike guides/patterns/topics, a partner's
   * name doesn't go stale within a session) — a no-op past the first
   * successful call. Failure leaves `this._partner` null, which
   * `_sharedGuidePartnerName` degrades to the generic "your partner" — never
   * a broken reader.
   * @returns {Promise<void>}
   */
  async _loadPartnerNameIfNeeded() {
    if (this._partner || this._partnerLoading) return;
    this._partnerLoading = true;
    try {
      const result = await this.discoveryAPI.getPartner();
      this._partner = result?.data || null;
    } catch (error) {
      console.error('[InsightsSection] Error loading partner for shared guides:', error);
    } finally {
      this._partnerLoading = false;
    }
  }

  /**
   * The couple partner's display name for shared-guide copy, falling back to
   * the generic "your partner" before `this._partner` loads or on failure.
   * @returns {string}
   */
  _sharedGuidePartnerName() {
    return this._partner?.firstName || this._partner?.name || this._partner?.username || 'your partner';
  }

  /**
   * Update guides UI after state change (full re-render, remounts filter bar)
   */
  updateGuidesUI() {
    if (this.currentTab !== 'guides') return;

    const contentContainer = this.container.querySelector('.understand-section__content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderGuidesContent();
      this._attachGuidesTabListeners();
    }
  }

  /**
   * Update only the guide cards list (after filter/sort change)
   */
  updateGuidesList() {
    const cardsContainer = this.container.querySelector('[data-tab-content="guides"] .session-cards-container');
    if (cardsContainer) {
      cardsContainer.innerHTML = this._renderGuidesBody();
    }
    if (this.guidesFilterBar) {
      this.guidesFilterBar.setCount(this.filteredGuides.length);
    }
  }

  /**
   * Mount the ResultsFilterBar for the guides tab
   */
  _mountGuidesFilterBar() {
    const mount = this.container.querySelector('[data-filter-bar-mount="guides"]');
    if (!mount) return;
    if (this.guidesFilterBar) {
      this.guidesFilterBar.destroy();
      this.guidesFilterBar = null;
    }
    this.guidesFilterBar = new ResultsFilterBar({
      container: mount,
      itemLabel: { singular: 'Guide', plural: 'Guides' },
      count: this.filteredGuides.length,
      filters: [
        {
          id: 'status',
          label: 'Status',
          defaultValue: this.guidesStatusFilter,
          options: [
            { value: 'active', label: 'Active' },
            {
              value: 'archived',
              label: 'Archived',
              itemLabel: { singular: 'Archived Guide', plural: 'Archived Guides' }
            }
          ]
        }
      ],
      actions: [
        { id: 'starred', label: 'Starred only', icon: 'star', pressed: this.guidesStarredFilter, onClick: () => this._toggleGuidesStarredFilter() }
      ],
      sort: {
        defaultValue: this.guidesSortOrder,
        options: [
          { value: 'newest', label: 'Newest' },
          { value: 'oldest', label: 'Oldest' }
        ]
      },
      onChange: (state) => this._handleGuidesFilterChange(state)
    });
  }

  /**
   * Toggle the guides-tab "Starred only" filter (local state only — no URL
   * persistence, matching this filter's lower-priority/nice-to-have scope).
   */
  _toggleGuidesStarredFilter() {
    this.guidesStarredFilter = !this.guidesStarredFilter;
    this.guidesFilterBar?.setActionPressed('starred', this.guidesStarredFilter);
    this.applyGuidesFilters();
    this.updateGuidesList();
  }

  /**
   * Handle filter/sort change from the guides filter bar
   * @param {{filters: Object, sort: string}} state
   */
  _handleGuidesFilterChange(state) {
    this.guidesStatusFilter = state.filters.status;
    this.guidesSortOrder = state.sort;
    // The starred toggle is section-owned, so a bar-side reset can only reach
    // us through the payload. Without this the star would visually clear while
    // the list stayed filtered.
    this.guidesStarredFilter = Boolean(state.actions?.starred);
    this.applyGuidesFilters();
    this.updateGuidesList();
  }

  /**
   * Derive one merged row per guide definition, apply the status and starred
   * filters, and sort — the single pass that produces `filteredGuides`.
   *
   * `this.guides` and `this.sharedGuides` stay the only sources of truth; the
   * rows built here are derived views over them, rebuilt on every star,
   * archive, and fetch. Nothing may resolve a guide OUT of `filteredGuides`:
   * it is a filtered view, and a row the active filters hide is simply absent
   * from it (`_guideRow` is the lookup every click and panel path uses).
   *
   * The status axis is disjoint (Active hides archived, Archived shows ONLY
   * archived) — the same shape /practices and the partner-notes list use, and
   * what makes the Archive action mean something on this list. A card is
   * Archived only when EVERY present half is (D8), so a couple's reading that
   * lands after you archived your own resurfaces here under Active.
   *
   * Never needs an explicit deletedAt guard — the server
   * (`getAvailableGuides`) already excludes soft-deleted rows entirely, so
   * `this.guides` never contains one.
   */
  applyGuidesFilters() {
    const wantArchived = this.guidesStatusFilter === 'archived';
    const rows = new Map();

    for (const guide of this.guides) {
      // status: null means no UserGuide row exists. Such a row is still listed
      // when it is generatable (blue "Generate" rail word, `canGenerateGuide`)
      // or locked (gray "Locked", `isLockedGuide`) — the Library shows what it
      // will hold and what unlocks it. Visibility is the server's: every row
      // in `this.guides` is already a visible definition. What remains hidden
      // is the residue — a status-null row that is neither, which today is
      // only a generation-mode mismatch.
      if (!guide.status && !InsightsSection.canGenerateGuide(guide) && !InsightsSection.isLockedGuide(guide)) continue;
      rows.set(guide.id, InsightsSection._mergedGuideRow(guide.id, guide, null));
    }

    for (const shared of (this.sharedGuides || [])) {
      const id = shared?.guideDefinitionId;
      if (!id) continue;
      // A definition whose own half is locked (or never generated) still gets a
      // card when the couple's half exists — Together-only, per D19. The
      // availability row travels with it so the reader can show the locked /
      // generate block in the You part.
      const individual = rows.get(id)?.individual || this.guides.find(g => g.id === id) || null;
      rows.set(id, InsightsSection._mergedGuideRow(id, individual, shared));
    }

    this.filteredGuides = [...rows.values()].filter((row) => {
      if (row.archived !== wantArchived) return false;
      // Starred acts on the `UserGuide` row: the couple's artifact carries no
      // star of its own, so a Together-only card never survives this filter.
      if (this.guidesStarredFilter && !row.individual?.starred) return false;
      return true;
    });

    this.filteredGuides.sort((a, b) => {
      const ka = InsightsSection._guideSortKey(a);
      const kb = InsightsSection._guideSortKey(b);
      // "Needs your review" is the only row on this tab that is waiting on the
      // user, so it pins above everything regardless of date or sort order.
      if (ka.pinned !== kb.pinned) return ka.pinned ? -1 : 1;
      // Locked rows close the list: they are the one thing here the reader
      // cannot act on from this tab, so everything generatable leads.
      if (ka.locked !== kb.locked) return ka.locked ? 1 : -1;
      if (ka.dated !== kb.dated) return ka.dated ? -1 : 1; // undated always at end
      if (!ka.dated && !kb.dated) return ka.title.localeCompare(kb.title);
      const delta = new Date(kb.at) - new Date(ka.at);
      return this.guidesSortOrder === 'newest' ? delta : -delta;
    });
  }

  /**
   * Sort key for one merged row.
   *
   * The date is the NEWEST half's `generatedAt` — the same stamps
   * `_guideRowDate` reads; a sealed-but-unwritten shared half contributes
   * nothing. An actionable row hides that date behind its rail word
   * (`renderGuideCard`'s either/or) yet is still ordered by it, so under
   * "Newest first" a "New" row can sit above an older dated one with no
   * visible explanation — an accepted cost of the right column meaning one
   * thing. "Needs review" pins regardless — it is the one row waiting on the
   * user; a locked row (`isLockedGuide`, no shared half) sinks regardless — it
   * is the one row the user cannot act on from here.
   *
   * @param {Object} row - Merged guide row
   * @returns {{pinned: boolean, locked: boolean, dated: boolean, at: string|null, title: string}}
   */
  static _guideSortKey(row) {
    const stamps = [
      row.individual?.generatedAt,
      row.shared?.generatedAt
    ].filter(Boolean).map(at => new Date(at).getTime()).filter(t => !Number.isNaN(t));

    return {
      pinned: row.shared?.viewerState === 'pending-review',
      locked: !row.shared && InsightsSection.isLockedGuide(row.individual),
      dated: stamps.length > 0,
      at: stamps.length > 0 ? new Date(Math.max(...stamps)).toISOString() : null,
      title: row.individual?.title || row.shared?.title || ''
    };
  }

  /**
   * Handle generate guide action — the first generation of an unlocked guide,
   * or a retry of an ERROR / stale-GENERATING one. READY guides are immutable
   * and have no regenerate affordance, so they never reach here.
   *
   * One call covers both cases: `generateGuide` creates the row when none
   * exists and reclaims a non-READY one when it does.
   * @param {string} guideId - Guide definition ID
   */
  async handleGenerateGuide(guideId) {
    // Per-guide, not global: the backend claim fence is per-definition, so
    // one guide writing is no reason to ignore a press on another.
    if (this.generatingGuideId === guideId) return;

    this.generatingGuideId = guideId;
    // The card and the open reader both read `_effectiveIndividualStatus`, so
    // this one flag puts the (writing) pill and the writing block on screen
    // before the call has even left.
    this.updateGuidesUI();
    this._rebuildOpenGuidePanel(guideId);

    try {
      const result = await API.generateGuide(guideId);

      if (!result || result.success === false) {
        throw new Error(result?.error || 'Generation failed');
      }

      // Refresh guides list to get updated status
      await this.loadGuidesData();
    } catch (error) {
      console.error('[InsightsSection] Error generating guide:', error);
      // Reachable by real users: eligibility can change between render and
      // click (assertCanGenerate throws GUIDE_LOCKED / GUIDE_NOT_VISIBLE),
      // and the generation call itself can fail.
      window.notificationManager?.showError(
        "Couldn't generate the guide. Please try again."
      );
    } finally {
      this.generatingGuideId = null;
      this.updateGuidesUI();
      // The reader that started this (the primary path) must not keep showing
      // its pre-generation body: `loadGuidesData` re-renders the LIST only,
      // and `_resolvePendingDetailFromURL` sees the wanted guide already open
      // and returns. Rebuild from the fresh row — GENERATING on success, the
      // persisted state (with its retry) on failure.
      this._rebuildOpenGuidePanel(guideId);
    }
  }

  /**
   * Continue the discovery path that unlocks a guide — the reader's locked
   * plate CTA (`[data-guide-continue-path]`, wired in `_wireGuidePartActions`).
   * @param {string} guideId - Guide definition ID
   */
  handleContinueGuidePath(guideId) {
    const guide = this.guides.find(item => item.id === guideId);
    if (!guide?.pathId) return;

    this.eventBus.emit('navigate', {
      page: 'chat',
      mode: 'DISCOVERY',
      pathId: guide.pathId,
      pathMetadata: guide.pathMetadata || {
        id: guide.pathId,
        name: guide.title
      }
    });
  }

  /**
   * Handle guide card click — every card, in every state, does ONE thing:
   * open that guide's detail page. Generation, retry, and the locked
   * explanation all live in the reader (`renderGuideYouPart`'s state blocks,
   * wired by `_wireGuidePartActions`), never on the card — so the rail word,
   * which content-row.css hides on hover, is never the only affordance.
   *
   * Resolved from the MERGED row, never `this.guides.find` alone: a
   * Together-only card (your reading archived, or never generated) has no
   * availability row worth reading and must still open (D19).
   * @param {string} definitionId - Guide definition ID
   */
  handleGuideCardClick(definitionId) {
    if (!this._guideRow(definitionId)) return;
    this.handleViewGuide(definitionId);
  }

  /**
   * Open the merged reader for one guide definition — push URL, then open the
   * detail panel (D12).
   *
   * The couple's half is already in memory (`this.sharedGuides`); the viewer's
   * own half is fetched. So the panel opens IMMEDIATELY with the full Together
   * part and a loading block where the You part will go, and the You part is
   * swapped in when the fetch settles:
   *
   *   1. Read the merged row. Open whenever it exists — a Together-only card,
   *      a locked card, a never-written card: the reader carries a state
   *      block for each, so nothing a card can show refuses to open.
   *   2. Emit `right-panel:open` with the merged payload. `_activeGuideId()`
   *      now names this guide; that single check, after the await, is what
   *      cancels a pending swap for a guide the user has since navigated away
   *      from.
   *   3. `API.getGuide` NEVER rejects — it resolves to `null` or an error
   *      envelope — so the swap branches on the VALUE, not on a catch:
   *      unusable → the You part's retry block; usable → the loaded sections,
   *      a patched masthead stamp, and a re-wired switch (the swap replaces the
   *      trailer that carries it).
   *   4. Refresh the header Contents menu, which needs the loaded section list
   *      that did not exist at panel-open time.
   *   5. Arm the D5 mark-seen observer on the Together part's bounded sentinel.
   *      A Together-only open has no fetch, so it arms at step 2 instead.
   *
   * @param {string} guideId - Guide definition ID (slug like 'guide-values-vision')
   * @param {Object} [options]
   * @param {boolean} [options.skipUrlPush=false] - Set when called from
   *   applyURLParams (URL is already correct).
   * @param {'you'|'together'|null} [options.pendingPart=null] - The part a
   *   deprecated `?sharedGuide=` deep link asked for (D10). Stamped onto the
   *   rendered body and consumed once the panel mounts.
   */
  async handleViewGuide(guideId, { skipUrlPush = false, pendingPart = null } = {}) {
    if (!guideId) return;
    // A new open supersedes any sentinel armed for the previous one, on every
    // entry (D5) — including the URL-push pass that immediately re-enters.
    this._disconnectTogetherObserver();
    this._disposeContentsSpy();
    if (this._panelOpen && this._panelDetail?.type === 'guide' && this._panelDetail.id === guideId) {
      // Toggle on the user-click path only — see handlePatternCardClick.
      if (!skipUrlPush) this.eventBus.emit('right-panel:close');
      return;
    }

    if (!skipUrlPush) {
      const url = new URL(window.location.href);
      url.searchParams.set('guide', guideId);
      url.searchParams.delete('pattern');
      url.searchParams.delete('sharedGuide');
      url.searchParams.delete('framework');
      url.searchParams.delete('topic');
      const target = url.pathname + url.search;
      if (this._panelOpen) {
        page.replace(target);
      } else {
        page(target);
      }
      return;
    }

    const row = this._guideRow(guideId);
    if (!row) return;
    // The only gate is that the row EXISTS: a card in any state opens, and the
    // reader renders whatever the merged row supports (D19 admits a
    // Together-only card; locked / never-written / GENERATING / ERROR rows get
    // their state block in the You part). Readability decides only whether
    // there is a `UserGuide` document to fetch.
    const individualReadable = row.individual?.status === 'READY';

    this.selectedPatternData = null;
    this.selectedGuide = null;
    this._panelDetail = { type: 'guide', id: guideId };
    // Opening a READY reading marks the viewer's OWN half read. A GENERATING
    // or ERROR row also opens now, but there is nothing to have read yet, so
    // its `viewedAt` stays untouched until the finished guide is opened. The
    // couple's half has its own acknowledgement inside the reader.
    if (individualReadable) this.markGuideViewed(guideId);
    this.updateGuidesList();

    this._panelOpen = true;
    this.eventBus.emit('right-panel:open', this._buildMergedGuidePanelPayload(row, { pendingPart }));
    this._consumePendingGuidePartStamp();
    this._armContentsSpy();

    if (!individualReadable) {
      // Nothing of your own to fetch (Together-only, locked, never written,
      // GENERATING, ERROR): the sentinel is already mounted and this is the
      // last moment in the open sequence.
      this._armTogetherSeenObserver(guideId);
      return;
    }

    const result = await API.getGuide(guideId);
    // The panel has moved on (another guide opened, or it closed) — discard.
    if (this._activeGuideId() !== guideId) return;

    const failed = !result || result.success === false || !result.content?.sections;
    if (!failed) this.selectedGuide = result;
    // On failure the availability row is still the best identity we hold: it
    // carries the title and status the retry block and the stamp read.
    const individual = failed ? row.individual : result;
    const shared = this._guideRow(guideId)?.shared || row.shared;

    const partEl = this._detailContentEl?.querySelector(`#${GUIDE_PART_YOU_ID}`);
    if (partEl) {
      partEl.outerHTML = renderGuideYouPart({
        individual,
        shared,
        partnerName: this._sharedGuidePartnerName(),
        failed
      });
      patchMergedGuideStamp(this._detailContentEl, { individual, shared });
      // The one preview gate may live at the end of the Together part, which
      // the swap leaves alone — and the You half's locked sections are only
      // known now.
      patchMergedGuideGate(this._detailContentEl, { individual, shared });
      // The rail's You group opened against the availability row (zero rows);
      // now that `content.sections` has loaded, refill it in place.
      patchGuideContentsRail(this._detailContentEl, contentsGroups({ individual, shared }));
      // `patchGuideContentsRail` replaced the rail node the spy was painting.
      this._armContentsSpy();
      // The swap replaced the node the sharing switch lives on (the Together
      // trailer rides inside the You part), so re-wire the fresh one.
      const freshPart = this._detailContentEl.querySelector(`#${GUIDE_PART_YOU_ID}`);
      wireMergedGuideBody(freshPart, {
        onToggleEnabled: (nextEnabled) => this._handleToggleSharedGuideEnabled(guideId, nextEnabled)
      });
    }

    this.eventBus.emit(
      'right-panel:set-header-menu',
      buildMergedGuideContentsMenu({ individual, shared })
    );

    // Last: the You part has swapped in, so the Together sentinel now sits at
    // its final position in the document (D5).
    this._armTogetherSeenObserver(guideId);
  }

  /**
   * Read and clear `_pendingGuidePart` — the transient the deprecated
   * `?sharedGuide=` alias hands to the `?guide=` branch of the next dispatch
   * (D10). Cleared only where the branch acts on it, so a deep link that
   * arrives before the guides have loaded keeps its intent until the post-load
   * re-resolve can use it.
   * @returns {'you'|'together'|null} The requested part, or null
   */
  _takePendingGuidePart() {
    const part = this._pendingGuidePart;
    this._pendingGuidePart = null;
    return part;
  }

  /**
   * Consume the `data-guide-pending-part` stamp the deprecated `?sharedGuide=`
   * alias left on the freshly mounted reader (D10): scroll to that part when it
   * exists, then clear the stamp so a later rebuild can't replay the jump.
   *
   * When the Together part is absent (waiting, withdrawn, still being written)
   * the You part's closing trailer is what the reader lands on and the intent
   * is simply dropped — there is nothing to scroll to and nothing to explain.
   */
  _consumePendingGuidePartStamp() {
    const root = this._detailContentEl?.querySelector('[data-guide-pending-part]');
    if (!root) return;
    const part = root.dataset.guidePendingPart;
    delete root.dataset.guidePendingPart;
    this._scrollToGuidePart(part);
  }

  /**
   * Scroll the open reader to one of its two parts, if that part is rendered.
   * Mirrors the Contents/rail jump behaviour (`bindGuideToc`), including the
   * reduced-motion opt-out; `scrollIntoView` is absent in jsdom, so the call is
   * feature-checked rather than assumed.
   * @param {'you'|'together'|null|undefined} part - Part to jump to
   */
  _scrollToGuidePart(part) {
    if (part !== 'you' && part !== 'together') return;
    const id = part === 'together' ? GUIDE_PART_TOGETHER_ID : GUIDE_PART_YOU_ID;
    const el = this._detailContentEl?.querySelector(`#${id}`);
    if (typeof el?.scrollIntoView !== 'function') return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }

  /**
   * Delegated handler for the reader's three in-body buttons: `Try again`
   * after a failed fetch OR a failed generation, `Write my guide` on an
   * unlocked-but-unwritten reading, `Continue the path` on a locked one, and
   * the "Together added" band's `Got it`. All live inside the panel content,
   * which this section owns and re-renders, so they are bound on the mounted
   * node rather than delegated from the document.
   * @param {HTMLElement|null} content - Mounted panel content node
   * @param {string} guideId - Guide definition ID
   */
  _wireGuidePartActions(content, guideId) {
    if (!content) return;
    content.addEventListener('click', (event) => {
      if (event.target.closest('[data-guide-together-ack]')) {
        event.preventDefault();
        // The explicit path (D5). It and the sentinel observer share one write,
        // so retire the observer here rather than let it fire into the guard.
        this._disconnectTogetherObserver();
        this._markTogetherSeen(guideId);
        return;
      }
      if (event.target.closest('[data-guide-retry]')) {
        event.preventDefault();
        // Two different retries share one button. A persisted ERROR row has
        // nothing to re-FETCH — the writing failed, so try writing again. A
        // failed fetch of a READY row re-runs the whole open sequence:
        // `_panelDetail` already names this guide, so clear it first or the
        // re-entry short-circuits on its own "already open" guard.
        if (this._guideRow(guideId)?.individual?.status === 'ERROR') {
          this.handleGenerateGuide(guideId);
          return;
        }
        this._panelDetail = null;
        this.handleViewGuide(guideId, { skipUrlPush: true });
        return;
      }
      if (event.target.closest('[data-guide-generate]')) {
        event.preventDefault();
        this.handleGenerateGuide(guideId);
        return;
      }
      if (event.target.closest('[data-guide-continue-path]')) {
        event.preventDefault();
        this.handleContinueGuidePath(guideId);
      }
    });
  }

  /**
   * Build the merged reader's `right-panel:open` payload for one card.
   *
   * Header chrome (star state, Archive/Unarchive label) is sourced from the
   * MERGED ROW — the already-loaded availability row plus the manifest item —
   * never from `this.selectedGuide`: that row's `.id` is the raw `UserGuide`
   * UUID rather than the guideDefinitionId, and it is still `null` when this
   * runs (the panel opens before `API.getGuide` resolves). The loaded row is
   * folded in once it exists so a rebuild after the swap keeps the sections
   * on screen.
   *
   * Star exists only when a `UserGuide` row does (`buildMergedGuideDetailPayload`
   * enforces it), so a Together-only card opens with a kebab and no star; a
   * locked or never-written card with no couple's half opens with neither —
   * there is no row to copy, share, or archive, the same rule as the card's
   * kebab.
   *
   * @param {Object} row - Merged guide row (see `_mergedGuideRow`)
   * @param {Object} [options]
   * @param {'you'|'together'|null} [options.pendingPart=null] - Part a
   *   deprecated `?sharedGuide=` link asked for; stamped on the rendered body
   *   for `_consumePendingGuidePartStamp` to act on once mounted (D10).
   * @returns {Object} right-panel payload
   */
  _buildMergedGuidePanelPayload(row, { pendingPart = null } = {}) {
    const { definitionId, shared } = row;
    const individual = this._mergedGuideIndividual(row);

    const payload = buildMergedGuideDetailPayload({
      individual,
      shared,
      pendingPart,
      partnerName: this._sharedGuidePartnerName(),
      onStar: (next) => this._toggleGuideStar(definitionId, next),
      onOption: (action) => this._handleGuidePanelOption(action, definitionId),
      onToggleEnabled: (nextEnabled) => this._handleToggleSharedGuideEnabled(definitionId, nextEnabled)
    });

    // The panel disposes the outgoing content's spy before it swaps or
    // closes; route it through the host's own disposer so the panel and the
    // host share ONE owner (a host-side dispose can never orphan the panel's).
    // Keyed on THIS payload's node: the panel's dispose lands 130ms late, by
    // which time the next reader's spy may already be armed.
    payload.onDispose = () => this._disposeContentsSpyFor(payload.content);

    this._detailContentEl = payload.content;
    this._wireGuidePartActions(payload.content, definitionId);
    return payload;
  }

  /**
   * The individual half as the reader should see it: the availability row with
   * the fetched `UserGuide` row folded over it once that has landed, and the
   * status overridden to GENERATING while a generate is in flight
   * (`_effectiveIndividualStatus`). Shared by the payload builder and every
   * in-place header refresh so a Contents menu rebuilt mid-reading can never
   * lose the You rows.
   * @param {Object} row - Merged guide row (see `_mergedGuideRow`)
   * @returns {Object|null} Individual half, or null when the card has none
   */
  _mergedGuideIndividual(row) {
    const loaded = this.selectedGuide
      && this.selectedGuide.guideDefinitionId === row.definitionId
      ? this.selectedGuide
      : null;
    const individual = row.individual && loaded
      ? { ...row.individual, ...loaded }
      : (loaded || row.individual);
    // Same in-flight override the card applies: while this section's own
    // generate call for the definition is running, the reader shows the
    // writing block rather than the pre-generation state the row still holds.
    if (individual && this._effectiveIndividualStatus(row) === 'GENERATING') {
      return { ...individual, status: 'GENERATING' };
    }
    return individual;
  }

  /**
   * Arm the single D5 mark-seen observer for the open reader.
   *
   * The sentinel is BOUNDED — the "Together added" band, else the Together
   * part's first section head — never the part itself: a reading taller than
   * the panel would never reach a 0.5 threshold, so a long Together part would
   * stay unread forever. `root` is the panel's own scroller when the content is
   * mounted inside one, and the viewport otherwise (which is also the jsdom
   * case). Nothing is armed unless the couple's half is actually unread, so a
   * re-read never re-writes `viewedAt`.
   *
   * @param {string} definitionId - Guide definition ID the reader is showing
   */
  _armTogetherSeenObserver(definitionId) {
    this._disconnectTogetherObserver();
    // jsdom (and any browser without the API) simply gets no observer — "Got
    // it" is still the explicit path, so nothing is stranded.
    if (typeof IntersectionObserver === 'undefined') return;
    if (!isTogetherUnread(this._guideRow(definitionId)?.shared)) return;

    const content = this._detailContentEl;
    const sentinel = content?.querySelector(`#${GUIDE_TOGETHER_BAND_ID}`)
      || content?.querySelector(`#${GUIDE_PART_TOGETHER_ID} .guide-section__head`);
    if (!sentinel) return;

    const root = content.closest?.('.right-panel__content') || null;
    this._togetherObserver = new IntersectionObserver((entries) => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      this._disconnectTogetherObserver();
      this._markTogetherSeen(definitionId);
    }, { root, threshold: 0.5 });
    this._togetherObserver.observe(sentinel);
  }

  /** Tear down the mark-seen observer, if one is armed. Idempotent. */
  _disconnectTogetherObserver() {
    this._togetherObserver?.disconnect();
    this._togetherObserver = null;
  }

  /**
   * Arm the contents rail's scroll-spy over the mounted reader, superseding
   * whatever was armed before. Called after the open emit, after the D12
   * You-part swap replaces the rail node, after the mark-seen patch clears
   * the Together heading's dot, and after a panel rebuild — every moment the
   * rail's rows change identity.
   */
  _armContentsSpy() {
    this._disposeContentsSpy();
    this._contentsSpyContentEl = this._detailContentEl;
    this._contentsSpyDisposer = armGuideContentsSpy(this._detailContentEl);
  }

  /**
   * Dispose the contents-rail spy ONLY if it is still the one armed over
   * `contentEl` — the payload `onDispose` path.
   *
   * `RightPanelManager.open()` schedules the cross-fade and hands control back
   * at once, so the host arms the incoming reader's spy immediately while the
   * OUTGOING payload's `onDispose` does not run until `_setContent` 130ms
   * later. An unconditional dispose there would kill the spy of the guide now
   * on screen (guide → guide swaps, and any drill/pop carrying a disposer).
   *
   * @param {Element|null} contentEl - The content node the caller owns
   */
  _disposeContentsSpyFor(contentEl) {
    if (!contentEl || contentEl !== this._contentsSpyContentEl) return;
    this._disposeContentsSpy();
  }

  /** Tear down the contents-rail spy, if one is armed. Idempotent. */
  _disposeContentsSpy() {
    this._contentsSpyDisposer?.();
    this._contentsSpyDisposer = null;
    this._contentsSpyContentEl = null;
  }

  /**
   * Mark the couple's half of one guide seen — the single write both D5 paths
   * (the sentinel observer and the band's "Got it") funnel through.
   *
   * Success-gated exactly like `markGuideViewed`: only a response carrying
   * `viewedAt` clears anything. On failure the band, the card's `together:`
   * badge key, and both unread dots — the header Contents pill's and the
   * rail's Together heading's — stay exactly as they were, so the next open
   * still offers the acknowledgement.
   *
   * @param {string} definitionId - Guide definition ID
   * @returns {Promise<void>}
   */
  async _markTogetherSeen(definitionId) {
    if (this._togetherSeenInFlight) return;
    if (!isTogetherUnread(this._guideRow(definitionId)?.shared)) return;

    this._togetherSeenInFlight = true;
    try {
      const result = await API.markSharedGuideViewed(definitionId);
      if (!result?.viewedAt) return;

      this.sharedGuides = this.sharedGuides.map(g => (g.guideDefinitionId === definitionId
        ? { ...g, viewedAt: result.viewedAt }
        : g));
      // BadgeService clears `cards.insights['together:'+id]`, which re-renders
      // the card through the section's `badge:state-changed` listener.
      emitContentViewed('sharedGuide', definitionId, true);
      // `filteredGuides` holds derived rows — re-derive rather than patch.
      this.applyGuidesFilters();

      // Everything below writes into the MOUNTED reader, and this method is
      // fire-and-forget across an await: by now the panel may have moved on to
      // another guide, and `this._detailContentEl` would be that guide's node.
      // Every in-place write belongs behind this one gate.
      const row = this._guideRow(definitionId);
      if (this._activeGuideId() === definitionId && row) {
        // The Contents button's dot reads `isTogetherUnread` too, so refresh
        // the header menu in place rather than rebuilding the whole reader.
        this.eventBus.emit(
          'right-panel:set-header-menu',
          buildMergedGuideContentsMenu({ individual: this._mergedGuideIndividual(row), shared: row.shared })
        );
        // Remove the band where it stands: a full re-render would throw away
        // the reader's scroll position mid-reading.
        this._detailContentEl?.querySelector(`#${GUIDE_TOGETHER_BAND_ID}`)?.remove();

        // The rail's Together heading wears the same unread dot, so it has to
        // clear here too. The individual half must come from
        // `_mergedGuideIndividual` — `row.individual` is the availability row
        // and carries no `content.sections`, which would wipe the You group's
        // rows mid-read.
        if (patchGuideContentsRail(
          this._detailContentEl,
          contentsGroups({ individual: this._mergedGuideIndividual(row), shared: row.shared })
        )) {
          // The patch replaced the rail node the spy was painting.
          this._armContentsSpy();
        }
      }
    } catch (error) {
      console.error('[InsightsSection] Error marking the shared guide seen:', error);
    } finally {
      this._togetherSeenInFlight = false;
    }
  }

  /**
   * Host callback for the shared-guide detail panel's enable/disable toggle
   * (`PATCH /couples/shared-guides/:guideDefinitionId/enabled`). Patches the
   * updated manifest item back into `this.sharedGuides` on success and
   * re-renders the list (the card's own status pill doesn't depend on
   * `enabled`, but keeping the cached array fresh means a reopened panel
   * never shows a stale value). Resolves the boolean the drill helper's
   * optimistic-toggle wiring reverts on.
   *
   * **Open-panel refresh (Codex major #4):** a successful write can change
   * more than the switch — revoking a sealed guide moves its viewerState to
   * `withdrawn` (a different body entirely), and re-enabling moves it back.
   * When the toggled guide is the one currently open, this rebuilds the WHOLE
   * detail payload from the fresh manifest item and re-emits `right-panel:open`
   * — the same "rebuild in place while open" idiom `CONTENT_ARCHIVED`'s handler
   * already uses for the plain guide panel (`right-panel:open` on an
   * already-open panel cross-fades in `RightPanelManager.open`, it does not
   * re-slide). The in-flight click handler's own old-button follow-up
   * (`applyState`/`btn.disabled = false`) still runs afterward on the now-
   * detached original node — harmless, since the fresh node it already
   * replaced carries the correct state.
   * @param {string} guideDefinitionId
   * @param {boolean} nextEnabled
   * @returns {Promise<boolean>} Whether the write succeeded
   */
  async _handleToggleSharedGuideEnabled(guideDefinitionId, nextEnabled) {
    try {
      const result = await API.setSharedGuideEnabled(guideDefinitionId, nextEnabled);
      if (!result || result.success === false) return false;
      this.sharedGuides = this.sharedGuides.map(g =>
        g.guideDefinitionId === guideDefinitionId ? result : g
      );
      if (this.isActive) this.updateGuidesList();
      // Turning sharing off is a cross-part edit — the Together part leaves and
      // the trailer appears — so the whole reader is rebuilt from the merged
      // row (already carrying the patched manifest item) and re-emitted.
      this._rebuildOpenGuidePanel(guideDefinitionId);
      return true;
    } catch (error) {
      console.error('[InsightsSection] Error toggling shared guide enabled:', error);
      return false;
    }
  }

  /**
   * Thin, guide-only equivalent of `SessionsSection._runBulk` — star or
   * archive a set of the viewer's OWN guides (keyed by guideDefinitionId,
   * NEVER the row UUID) and patch `this.guides` locally on success.
   * Deliberately smaller than porting SessionsSection's full multi-type
   * fan-out: InsightsSection only ever acts on guides.
   *
   * This is the individual half alone. Archive on a `/library` card acts on
   * every present half, so the card's Archive routes through
   * `_setGuideRowArchived`, which calls this for the `UserGuide` row and the
   * shared-guide route for the couple's — see D8. There is no delete action:
   * no guide surface offers Delete.
   *
   * @param {'star'|'archive'} action
   * @param {string[]} guideDefinitionIds
   * @param {Object} [opts]
   * @param {boolean} [opts.archived] - Explicit archive direction. Without it
   *   the direction toggles off the batch's own state, which is right for a
   *   standalone star/archive but wrong for the merged card, where the
   *   direction is decided once for both halves.
   * @param {boolean} [opts.notify=true] - Whether this call owns the archive
   *   toast. False when the caller is archiving more than one half and will
   *   speak for the whole card itself.
   * @returns {Promise<boolean>} whether the action ran AND succeeded (false on
   *   an unknown id or a failure envelope)
   */
  async _runGuideBulk(action, guideDefinitionIds, { archived, notify = true } = {}) {
    const items = this.guides.filter(g => guideDefinitionIds.includes(g.id));
    if (items.length === 0) return false;

    // Every branch applies local state FIRST, then broadcasts per id on the
    // contentSync bridge (confirmed success only). Apply-local-first is what
    // makes this section's own listeners self-skip: by the time the event
    // lands, the row already matches, so each handler hits its idempotency
    // guard and bails.
    let result;
    if (action === 'star') {
      const starred = !items.every(g => g.starred);
      result = await API.starGuides(guideDefinitionIds, starred);
      if (!result || result.success === false) return false;
      const stamp = starred ? new Date().toISOString() : null;
      this.guides = this.guides.map(g => guideDefinitionIds.includes(g.id)
        ? { ...g, starred, starredAt: stamp }
        : g);
      guideDefinitionIds.forEach(id => emitContentStarred('guide', id, starred));
    } else if (action === 'archive') {
      const next = archived === undefined ? !items.every(g => g.archived) : archived;
      result = await API.archiveGuides(guideDefinitionIds, next);
      // `false` here reaches no user-facing surface on its own — the row-menu
      // caller ignores the return and the panel caller uses it only to gate a
      // close — so this branch owns its own failure toast.
      if (!result || result.success === false) {
        notifyArchiveFailed({ count: guideDefinitionIds.length, type: 'guide', archived: next });
        return false;
      }
      const stamp = next ? new Date().toISOString() : null;
      this.guides = this.guides.map(g => guideDefinitionIds.includes(g.id)
        ? { ...g, archived: next, archivedAt: stamp }
        : g);
      guideDefinitionIds.forEach(id => emitContentArchived('guide', id, next));
      if (notify) notifyArchived({ count: guideDefinitionIds.length, type: 'guide', archived: next });
    }

    this.applyGuidesFilters();
    this.updateGuidesList();
    return true;
  }

  /**
   * Archive, or restore, every present half of one `/library` guide card (D8).
   *
   * Archive is per person and per half — `UserGuide.archivedAt` for your own
   * reading, `SharedGuideParticipant.archivedAt` for the couple's — and this
   * drives both in the SAME direction, so the card lands wholly in Active or
   * wholly in Archived. Your partner's archive state is never touched and
   * never serialized.
   *
   * The two writes are independent, and neither API call throws: each half is
   * patched only from its own confirmed response, so a half that fails stays
   * where it was and the card recomputes honestly (a card is Archived only
   * when every present half is). One toast speaks for the whole card.
   *
   * @param {string} definitionId - Guide definition ID
   * @param {boolean} archived - true to archive, false to restore
   * @returns {Promise<boolean>} Whether every present half was written
   */
  async _setGuideRowArchived(definitionId, archived) {
    const row = this._guideRow(definitionId);
    if (!row) return false;

    const hasOwnRow = InsightsSection._hasUserGuideRow(row.individual);
    const hasShared = Boolean(row.shared);
    if (!hasOwnRow && !hasShared) return false;

    // The individual half owns the toast only when it is the whole card.
    const ownOk = hasOwnRow
      ? await this._runGuideBulk('archive', [definitionId], { archived, notify: !hasShared })
      : true;

    let sharedOk = true;
    if (hasShared) {
      const result = await API.setSharedGuideArchived(definitionId, archived);
      sharedOk = Boolean(result) && result.success !== false;
      if (sharedOk) {
        this.sharedGuides = this.sharedGuides.map(g => (g.guideDefinitionId === definitionId
          ? { ...g, archivedAt: result.archivedAt ?? (archived ? new Date().toISOString() : null) }
          : g));
        emitContentArchived('sharedGuide', definitionId, archived);
      }
      // One toast for the whole card, and only when every present half was
      // written — a half left behind must not read as success.
      if (ownOk && sharedOk) {
        notifyArchived({ count: 1, type: 'guide', archived });
      } else {
        notifyArchiveFailed({ count: 1, type: 'guide', archived });
      }
      this.applyGuidesFilters();
      this.updateGuidesList();
    }

    // The panel STAYS OPEN and its Archive/Unarchive label is patched (D13) —
    // archiving is not a reason to lose your place in the reading. Done here,
    // not at the two call sites, so the row kebab and the panel dropdown can't
    // drift; the `content:archived` listener self-skips because the local rows
    // already match by the time it fires.
    this._rebuildOpenGuidePanel(definitionId);
    return ownOk && sharedOk;
  }

  /**
   * Re-emit the open reader for `definitionId` from the current merged row, if
   * that is what the panel is showing. `right-panel:open` on an already-open
   * panel cross-fades in `RightPanelManager.open` — it does not re-slide.
   * @param {string} definitionId - Guide definition ID
   */
  _rebuildOpenGuidePanel(definitionId) {
    if (!this._panelOpen || this._activeGuideId() !== definitionId) return;
    const row = this._guideRow(definitionId);
    if (!row) return;
    this.eventBus.emit('right-panel:open', this._buildMergedGuidePanelPayload(row));
    // The rebuild replaced the node the sentinel lived on — and the rail.
    this._armTogetherSeenObserver(definitionId);
    this._armContentsSpy();
  }

  /**
   * Detail-panel header star toggle. Operates on the guideDefinitionId
   * captured at panel-open time (the `guideId` param), never re-derived from
   * `this.selectedGuide` later — see `_buildMergedGuidePanelPayload`'s
   * docstring. Star acts on the `UserGuide` row only.
   * @param {string} guideId - Guide definition ID
   * @param {boolean} next - Desired starred state
   * @returns {Promise<boolean>} whether the toggle succeeded and matches `next`
   */
  async _toggleGuideStar(guideId, next) {
    const ok = await this._runGuideBulk('star', [guideId]);
    if (!ok) return false;
    const fresh = this.guides.find(g => g.id === guideId);
    return !!fresh && fresh.starred === next;
  }

  /**
   * Detail-panel Options-dropdown handler — Archive/Unarchive, the one
   * management action any guide surface offers.
   *
   * The panel STAYS OPEN and its label is patched (D13): archiving is not a
   * reason to lose your place in the reading. The archived row does leave the
   * Active list behind the still-open panel (`applyGuidesFilters` runs inside
   * the write), which is the intended pair to the Status filter.
   * @param {'archive'} action
   * @param {string} guideId - Guide definition ID
   */
  async _handleGuidePanelOption(action, guideId) {
    if (action !== 'archive') return;
    const before = this._guideRow(guideId);
    if (!before) return;
    await this._setGuideRowArchived(guideId, !before.archived);
  }

  /**
   * Persist and locally clear the viewed badge on the viewer's OWN half of a
   * guide. The couple's half has its own view endpoint and its own badge key
   * (D3); nothing here reaches across to it.
   * @param {string} guideId - Guide definition ID
   */
  async markGuideViewed(guideId) {
    if (!guideId) return;

    const updated = await API.markGuideViewed(guideId);
    if (!updated) return;
    emitContentViewed('guide', guideId, true);

    this.guides = this.guides.map(guide =>
      guide.id === guideId ? { ...guide, viewedAt: updated.viewedAt } : guide
    );
    // `filteredGuides` holds derived rows, so it is re-derived rather than
    // patched — patching it by `.id` would silently miss every row now.
    this.applyGuidesFilters();
  }

  /**
   * Load the user's clinical assessment results from the API.
   */
  async loadFrameworksData() {
    this.frameworksLoading = true;
    this.frameworksError = null;
    this.updateFrameworksUI();

    try {
      const results = await API.getFrameworkResults();

      // Guard the shape, not just truthiness — error envelopes can be truthy.
      if (!Array.isArray(results)) {
        throw new Error('Failed to fetch assessment results');
      }

      this.frameworks = results;
      this.frameworksLoaded = true;
      this.frameworksLoading = false;
      this.applyFrameworksSort();
      this.updateFrameworksUI();

      // Re-resolve any URL detail param that wasn't resolvable when
      // applyURLParams first ran (because results hadn't loaded yet).
      this._resolvePendingDetailFromURL();
    } catch (error) {
      console.error('[InsightsSection] Error loading assessment results:', error);
      this.frameworksLoading = false;
      this.frameworksError = error.message;
      this.updateFrameworksUI();
    }
  }

  /**
   * Update frameworks UI after state change (full re-render)
   */
  updateFrameworksUI() {
    if (this.currentTab !== 'assessments') return;

    const contentContainer = this.container.querySelector('.understand-section__content');
    if (contentContainer) {
      contentContainer.innerHTML = this.renderFrameworksContent();
      this._attachFrameworksTabListeners();
    }
  }

  /**
   * Re-render just the framework rows (keeps active-highlight in sync on
   * panel open/close).
   */
  updateFrameworksList() {
    const cardsContainer = this.container.querySelector('[data-tab-content="assessments"] .session-cards-container');
    if (cardsContainer) {
      cardsContainer.innerHTML = this._renderFrameworksBody();
    }
    if (this.frameworksFilterBar) {
      this.frameworksFilterBar.setCount(this.filteredFrameworks.length);
    }
  }

  /**
   * Mount the ResultsFilterBar for the assessments (assessment results) tab
   */
  _mountFrameworksFilterBar() {
    const mount = this.container.querySelector('[data-filter-bar-mount="frameworks"]');
    if (!mount) return;
    if (this.frameworksFilterBar) {
      this.frameworksFilterBar.destroy();
      this.frameworksFilterBar = null;
    }
    this.frameworksFilterBar = new ResultsFilterBar({
      container: mount,
      itemLabel: { singular: 'Assessment', plural: 'Assessments' },
      count: this.filteredFrameworks.length,
      sort: {
        defaultValue: this.frameworkSortOrder,
        options: [
          { value: 'newest', label: 'Newest' },
          { value: 'oldest', label: 'Oldest' }
        ]
      },
      onChange: (state) => this._handleFrameworksFilterChange(state)
    });
  }

  /**
   * Handle sort change from the frameworks filter bar
   * @param {{sort: string}} state
   */
  _handleFrameworksFilterChange(state) {
    this.frameworkSortOrder = state.sort;
    this.applyFrameworksSort();
    this.updateFrameworksList();
  }

  /**
   * Sort filteredFrameworks by completion date per frameworkSortOrder.
   * (The API returns newest-first; this keeps the toggle authoritative.)
   */
  applyFrameworksSort() {
    this.filteredFrameworks = [...this.frameworks];
    this.filteredFrameworks.sort((a, b) => {
      const dateA = new Date(a.completedAt);
      const dateB = new Date(b.completedAt);
      return this.frameworkSortOrder === 'oldest' ? dateA - dateB : dateB - dateA;
    });
  }

  /**
   * Handle an assessment-result card click — push URL, then open detail panel.
   * Data is already loaded (cards only render from loaded results), so the
   * panel renders synchronously.
   *
   * @param {string} questionnaireId
   * @param {Object} [options]
   * @param {boolean} [options.skipUrlPush=false] - Set when called from
   *   applyURLParams (URL is already correct).
   */
  handleFrameworkCardClick(questionnaireId, { skipUrlPush = false } = {}) {
    if (this._panelOpen && this._panelDetail?.type === 'framework' && this._panelDetail.id === questionnaireId) {
      // Toggle on the user-click path only — see handlePatternCardClick.
      if (!skipUrlPush) this.eventBus.emit('right-panel:close');
      return;
    }

    const fw = this.frameworks.find(f => f.questionnaireId === questionnaireId);
    if (!fw) return;

    if (!skipUrlPush) {
      const url = new URL(window.location.href);
      url.searchParams.set('framework', questionnaireId);
      url.searchParams.delete('pattern');
      url.searchParams.delete('guide');
      url.searchParams.delete('sharedGuide');
      url.searchParams.delete('topic');
      const target = url.pathname + url.search;
      if (this._panelOpen) {
        page.replace(target);
      } else {
        page(target);
      }
      return;
    }

    this.selectedGuide = null;
    this.selectedPatternData = null;
    this._panelDetail = { type: 'framework', id: questionnaireId };
    this.updateFrameworksList();

    const content = this._buildFrameworkDetailContent(fw);
    this._detailContentEl = content;

    this._panelOpen = true;
    this.eventBus.emit('right-panel:open', {
      // No chrome title — the in-panel title-block carries the framework name.
      title: '',
      primaryButton: {
        label: 'Discuss Assessment',
        onClick: () => this.handleDiscussAssessment(fw.questionnaireId)
      },
      content,
      contentType: 'library-framework'
    });
  }

  /**
   * Handle the "Discuss Assessment" action — navigates into an ADVICE
   * `discuss_framework` session. Identity only crosses (itemId + display-only
   * sourceItem); the backend reloads the canonical result server-side
   * (`AdviceStrategy._buildFrameworkDiscussionContext`). Mirrors
   * handleDiscussPattern's ADVICE `artifactDiscussion` shape.
   *
   * A parallel (not shared) module-level in-flight guard from
   * `launchPatternDiscussion`'s (patternDrillHelper.js) — each artifact type gets its own flag rather
   * than one shared boolean, so a double-click on one card can never suppress
   * a genuine click on the other's card. Same rationale as
   * `guideDrillHelper.js`'s `atomLaunchInFlight`: the eventBus dispatch below
   * is fire-and-forget, so without a guard a second click before the first
   * navigation lands would fire two launches.
   *
   * @param {string} questionnaireId - Clinical questionnaire id (e.g. 'ecr12')
   */
  handleDiscussAssessment(questionnaireId) {
    if (frameworkDiscussionLaunchInFlight) return;

    const fw = this.frameworks.find(f => f.questionnaireId === questionnaireId);
    if (!fw) return;

    frameworkDiscussionLaunchInFlight = true;

    this.eventBus.emit('navigate', {
      page: 'chat',
      mode: 'ADVICE',
      artifactDiscussion: {
        discriminator: 'discuss_framework',
        itemId: questionnaireId,
        sourceItem: {
          questionnaireId,
          title: fw.title
        }
      }
    });

    // The eventBus dispatch above is synchronous fire-and-forget — clear on
    // the next tick, once dispatch has fully unwound, so a rapid re-entrant
    // click within the same event-loop turn can't fire a second launch.
    setTimeout(() => { frameworkDiscussionLaunchInFlight = false; }, 0);
  }

  /**
   * Handle a topic card click — opens the right detail panel.
   * URL-routed via `?topic=<id>`.
   *
   * @param {string} topicId
   * @param {Object} [options]
   * @param {boolean} [options.skipUrlPush=false]
   */
  async handleTopicCardClick(topicId, { skipUrlPush = false } = {}) {
    if (this._panelOpen && this._panelDetail?.type === 'topic' && this._panelDetail.id === topicId) {
      // Toggle on the user-click path only — see handlePatternCardClick.
      if (!skipUrlPush) this.eventBus.emit('right-panel:close');
      return;
    }

    const topic = this.topics.find(t => t.id === topicId);
    if (!topic) return;

    if (!skipUrlPush) {
      const url = new URL(window.location.href);
      url.searchParams.set('topic', topicId);
      url.searchParams.delete('pattern');
      url.searchParams.delete('guide');
      url.searchParams.delete('sharedGuide');
      url.searchParams.delete('framework');
      const target = url.pathname + url.search;
      if (this._panelOpen) {
        page.replace(target);
      } else {
        page(target);
      }
      return;
    }

    this._panelDetail = { type: 'topic', id: topicId };
    this._clearTopicSummaryPoll();
    this.updateTopicsList();

    const content = document.createElement('div');
    content.innerHTML = this.renderTopicSummaryPanel(topic, null, { loading: true });
    this._detailContentEl = content;

    this._panelOpen = true;
    this.eventBus.emit('right-panel:open', {
      title: '',
      titleAlign: 'left',
      content,
      contentType: 'insights-topic'
    });

    this._loadTopicSummary(topic);
  }

  /**
   * Fetch (or regenerate) a topic's summary and render it into the open panel.
   * Guards against the panel having changed during the await.
   * @param {Object} topic
   * @param {{ regenerate?: boolean }} [opts]
   */
  async _loadTopicSummary(topic, { regenerate = false } = {}) {
    const data = regenerate
      ? await API.regenerateTopicSummary(topic.id)
      : await API.getTopicSummary(topic.id);

    // Panel may have closed or switched topics while we awaited.
    if (!this._panelOpen || this._panelDetail?.type !== 'topic' || this._panelDetail.id !== topic.id) return;
    if (!this._detailContentEl) return;

    if (!data) {
      this._detailContentEl.innerHTML = this.renderTopicSummaryPanel(topic, null, { error: true });
      this._attachTopicSummaryListeners(topic, null);
      return;
    }

    this._detailContentEl.innerHTML = this.renderTopicSummaryPanel(topic, data);
    this._attachTopicSummaryListeners(topic, data);

    // First view kicks a background generation; poll until it lands.
    if (data.status === 'GENERATING' && !data.summary) {
      this._scheduleTopicSummaryPoll(topic);
    }
  }

  /** Poll a generating summary a few times, then give up gracefully. */
  _scheduleTopicSummaryPoll(topic, attempt = 0) {
    this._clearTopicSummaryPoll();
    if (attempt >= 6) return; // ~18s ceiling
    this._topicSummaryPollTimer = setTimeout(async () => {
      if (!this._panelOpen || this._panelDetail?.id !== topic.id) return;
      const data = await API.getTopicSummary(topic.id);
      if (!this._panelOpen || this._panelDetail?.id !== topic.id || !this._detailContentEl) return;
      if (data && data.status !== 'GENERATING') {
        this._detailContentEl.innerHTML = this.renderTopicSummaryPanel(topic, data);
        this._attachTopicSummaryListeners(topic, data);
        return;
      }
      this._scheduleTopicSummaryPoll(topic, attempt + 1);
    }, 3000);
  }

  /** Clear any pending summary poll timer. */
  _clearTopicSummaryPoll() {
    if (this._topicSummaryPollTimer) {
      clearTimeout(this._topicSummaryPollTimer);
      this._topicSummaryPollTimer = null;
    }
  }

  /** Attach the in-panel "Regenerate" + scroll-to + open-thread listeners. */
  _attachTopicSummaryListeners(topic, data = null) {
    if (!this._detailContentEl) return;

    const btn = this._detailContentEl.querySelector('[data-action="regen-topic-summary"]');
    if (btn) {
      btn.addEventListener('click', () => {
        btn.disabled = true;
        const statusEl = this._detailContentEl.querySelector('.topic-summary__status');
        if (statusEl) statusEl.textContent = 'Refreshing…';
        this._clearTopicSummaryPoll();
        this._loadTopicSummary(topic, { regenerate: true });
      });
    }

    // Metadata count links scroll to their section (mirrors session-notes detail).
    this._detailContentEl.querySelectorAll('[data-scroll-to]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const target = this._detailContentEl.querySelector(`.${link.dataset.scrollTo}`);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    // Clinical-insight nav cards → swap to the reconstructed full detail panel.
    const clinicalInsights = data?.clinicalInsights || [];
    this._detailContentEl.querySelectorAll('.topic-insight-card[data-clinical-section]').forEach(el => {
      el.addEventListener('click', () => {
        const ci = clinicalInsights.find(c => c.sectionCode === el.dataset.clinicalSection);
        if (ci) this._showClinicalInsightDetail(ci);
      });
    });

    // Open-thread cards → open the thread directly in chat (matches the
    // session-notes explore-prompt behavior: ADVICE seeded with the thread).
    const threads = data?.openThreads || [];
    this._detailContentEl.querySelectorAll('.sd-explore-prompt[data-explore-id]').forEach(el => {
      el.addEventListener('click', () => {
        const thread = threads.find(t => t.id === el.dataset.exploreId);
        if (!thread) return;
        this.eventBus.emit('navigate', {
          page: 'chat',
          mode: 'ADVICE',
          force: true,
          openThreadId: thread.id,
          openThread: {
            id: thread.id,
            question: thread.question,
            context: thread.context,
            sessionId: thread.sessionId || null,
            observationId: thread.observationId || null
          }
        });
      });
    });
  }

  /**
   * Right-panel content for a topic summary. Handles loading / cold-start /
   * generating / ready / error states.
   * @param {Object} topic - { id, label, description, type }
   * @param {Object|null} data - summary response from the API
   * @param {{ loading?: boolean, error?: boolean }} [state]
   * @returns {string} HTML string
   */
  renderTopicSummaryPanel(topic, data, { loading = false, error = false } = {}) {
    const typeLabel = topic.type === 'life' ? 'Life topic' : 'Core topic';
    const title = `<h1 class="sd-title">${this.escapeHtml(topic.label)}</h1>`;
    const wrap = inner => `<div class="topic-detail topic-summary">${inner}</div>`;

    if (loading) {
      return wrap(`${title}<p class="sd-summary topic-summary__status"><em>Loading…</em></p>`);
    }
    if (error) {
      return wrap(`${title}<p class="sd-summary"><em>We couldn't load this summary. Please try again shortly.</em></p>`);
    }
    if (!data || data.hasData === false) {
      // Cold-start: invite, don't look broken.
      const descHtml = topic.description
        ? `<p class="sd-summary">${this.escapeHtml(topic.description)}</p>` : '';
      return wrap(`
        ${title}
        <div class="sd-metadata"><span class="sd-metadata__text">${typeLabel}</span></div>
        <div class="sd-section-header">Summary</div>
        ${descHtml}
        <p class="sd-summary topic-detail__empty"><em>We haven't explored this area together yet. Starting a conversation is the best way to begin building a picture here.</em></p>
      `);
    }

    const generating = data.status === 'GENERATING' && !data.summary;
    const obsCount = data.observations?.length || 0;
    const threadCount = data.openThreads?.length || 0;

    const metaItems = [
      `<span class="sd-metadata__text">${typeLabel}</span>`,
      `<span class="sd-metadata__text">${this.escapeHtml(this._overallCoverageLabel(data.overallCoverage))}</span>`,
      obsCount ? `<a class="sd-metadata__text" data-scroll-to="sd-observations"><b>${obsCount}</b> ${obsCount === 1 ? 'observation' : 'observations'}</a>` : '',
      threadCount ? `<a class="sd-metadata__text" data-scroll-to="sd-explore"><b>${threadCount}</b> ${threadCount === 1 ? 'open thread' : 'open threads'}</a>` : ''
    ].filter(Boolean).join('');

    const summaryHtml = generating
      ? '<p class="sd-summary topic-summary__status"><em>Putting your summary together…</em></p>'
      : String(data.summary || '')
          .split(/\n\s*\n/)
          .filter(Boolean)
          .map(p => `<p class="sd-summary">${this.escapeHtml(p)}</p>`)
          .join('');

    const observationsHtml = obsCount ? `
      <div class="sd-observations">
        <div class="sd-section-header">Observations</div>
        <div class="sd-blocks sd-blocks--list">
          ${data.observations.map(o => SessionsSection.renderObservationItem(o, {})).join('')}
        </div>
      </div>` : '';

    const threadsHtml = threadCount ? `
      <div class="sd-explore">
        <div class="sd-section-header">Open threads</div>
        ${data.openThreads.map(t => SessionsSection.renderExplorePrompt(t)).join('')}
      </div>` : '';

    return wrap(`
      ${title}
      <div class="sd-metadata">${metaItems}</div>
      <div class="sd-section-header">Summary</div>
      ${summaryHtml}
      ${this._renderTopicCoverage(data.subcategories)}
      ${this._renderClinicalInsightCards(data.clinicalInsights)}
      ${observationsHtml}
      ${threadsHtml}
      ${generating ? '' : this._renderTopicSummaryFooter(data)}
    `);
  }

  /**
   * Render the "Your assessments" section: a clickable nav card per
   * clinical-framework insight. Reuses the discovery insight-card look
   * (label + primary/secondary pills + finding bullets); clicking opens the
   * full reconstructed detail in the same panel.
   * @param {Array|undefined} clinicalInsights - from the detail API
   * @returns {string} HTML string
   */
  _renderClinicalInsightCards(clinicalInsights) {
    if (!clinicalInsights?.length) return '';
    const cards = clinicalInsights.map(ci => {
      const pills = [];
      if (ci.title) {
        pills.push(`<span class="structured-card__pill structured-card__pill--primary">${this.escapeHtml(ci.title)}${ci.secondaries?.length ? ' <span class="structured-card__pill-label">(Primary)</span>' : ''}</span>`);
      }
      for (const s of (ci.secondaries || [])) {
        pills.push(`<span class="structured-card__pill structured-card__pill--secondary">${this.escapeHtml(s)} <span class="structured-card__pill-label">(Secondary)</span></span>`);
      }
      const pillsHtml = pills.length ? `<div class="structured-card__pills">${pills.join('')}</div>` : '';
      const lead = ci.items?.[0];
      const leadHtml = lead?.header
        ? `<p class="topic-insight-card__lead">${this.escapeHtml(lead.header)}</p>`
        : '';
      return `
        <div class="card structured-card structured-card--insight structured-card--clickable topic-insight-card"
             data-clinical-section="${this.escapeHtml(ci.sectionCode)}" role="button" tabindex="0">
          <div class="structured-card__label-row">
            <div class="structured-card__label">${this.escapeHtml(ci.label || ci.subcategory)}</div>
            <span class="structured-card__learn-more">
              <span class="structured-card__learn-more-text">View full results</span>
              <svg class="structured-card__learn-more-chevron" width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </span>
          </div>
          ${pillsHtml}
          ${leadHtml}
        </div>`;
    }).join('');
    return `
      <div class="sd-section-header">Your assessments</div>
      <div class="topic-insight-cards">${cards}</div>`;
  }

  /**
   * Drill the right panel into a single clinical insight's full detail — the
   * exact Discovery "View full results" panel (structured score-bar detail), or
   * the persisted prose for frameworks without a composer. Uses the shared
   * `right-panel:drill` convention (same as pattern → session): the panel adds a
   * header back-chevron that pops back to the topic, preserving its scroll.
   * Deliberately excluded from the `right-panel:closed` prefix guard (its bare
   * `contentType: 'insight'` has no `library-`/`insights-` prefix) — it's a
   * drill sub-view, not a top-level panel.
   * @param {Object} ci - one entry from data.clinicalInsights
   */
  _showClinicalInsightDetail(ci) {
    const content = buildAssessmentDetailContent({
      detail: ci.format === 'structured' && ci.detail ? ci.detail : null,
      markdown: ci.markdown || ''
    });

    this.eventBus.emit('right-panel:drill', {
      title: ci.label || ci.subcategory,
      titleAlign: 'left',
      content,
      contentType: 'insight'
    });
  }

  /** Plain-language label for a topic's overall coverage. */
  _overallCoverageLabel(coverage) {
    if (coverage === 'good') return 'A clear picture';
    if (coverage === 'partial') return 'A partial picture';
    return 'Just getting started';
  }

  /**
   * Render the "What we've explored" per-subcategory breakdown: each sub-area
   * with a 3-dot meter + plain caption (no raw gap prose) and a subtle marker
   * when a structured assessment backs it.
   * @param {Array|undefined} subcategories - from the detail API
   * @returns {string} HTML string
   */
  _renderTopicCoverage(subcategories) {
    if (!subcategories?.length) return '';
    const META = {
      good: { level: 3, caption: 'A clear picture' },
      partial: { level: 2, caption: 'Coming into focus' },
      none: { level: 0, caption: 'Not explored yet' }
    };
    const rows = subcategories.map(s => {
      const { level, caption } = META[s.coverage] || META.none;
      const dots = [0, 1, 2]
        .map(i => `<span class="topic-card__dot${i < level ? ' topic-card__dot--on' : ''}"></span>`)
        .join('');
      const badge = s.hasClinical
        ? '<span class="topic-coverage__badge" title="Backed by a structured assessment">assessed</span>'
        : '';
      return `
        <div class="topic-coverage__row${s.coverage === 'none' ? ' topic-coverage__row--empty' : ''}">
          <span class="topic-coverage__name">${this.escapeHtml(s.name)}${badge}</span>
          <span class="topic-coverage__meter">
            <span class="topic-card__dots" aria-hidden="true">${dots}</span>
            <span class="topic-coverage__state">${this.escapeHtml(caption)}</span>
          </span>
        </div>`;
    }).join('');
    return `
      <div class="sd-section-header">What we've explored</div>
      <div class="topic-coverage">${rows}</div>`;
  }

  /** Status line + manual regenerate button beneath a ready summary. */
  _renderTopicSummaryFooter(data) {
    const isStale = data.status === 'STALE';
    const dateStr = data.generatedAt
      ? new Date(data.generatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : '';
    const statusText = isStale
      ? 'New insights have come in since this was written.'
      : (dateStr ? `Updated ${dateStr}` : '');
    return `
      <div class="topic-summary__footer">
        <span class="topic-summary__status">${this.escapeHtml(statusText)}</span>
        <button class="btn btn--secondary btn--small" data-action="regen-topic-summary">
          ${isStale ? 'Refresh' : 'Regenerate'}
        </button>
      </div>`;
  }

  /**
   * Show page info detail view, optionally scrolled to a specific tab.
   * @param {string} [activeTab=InsightsSection.DEFAULT_TAB] - Which sub-tab to activate
   */
  showPageInfo(activeTab = this.currentTab) {
    const content = buildPageInfoPanel({
      title: 'About Library',
      intro: `Library collects what your work here has uncovered — the areas of
        your life we're learning about, recurring patterns, tailored guides,
        and your assessment results — so you can return to them any time.`,
      tabs: InsightsSection.PAGE_INFO_TABS,
      activeTab
    });

    // Reading the full page explanation retires the inline "What are insights?"
    // hint on chat cards — otherwise the user is offered a primer they just read.
    window.helpHintService?.markDismissed?.('insights');

    this._panelOpen = true;
    this.eventBus.emit('right-panel:open', {
      content,
      // Literal, not derived: this section is Insights but its page is Library
      // and its contentType is `library-info` — the clearest case for why the
      // three page-info contentTypes are hardcoded per call site.
      contentType: 'library-info'
    });
  }

  /**
   * Close the detail right panel.
   */
  handleCloseDetail() {
    this.eventBus.emit('right-panel:close');
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    return escapeHtml(text);
  }

  /**
   * (Re)bind the sticky header stack to the current scroll root: title →
   * segment nav → the active tab's filter bar. Idempotent.
   *
   * Unlike the other list sections, this one renders EXACTLY ONCE (behind the
   * `isInitialized` guard) and keeps its DOM across deactivation — so the
   * activate() call site below, not render(), is what restores the stack on a
   * return visit.
   */
  _initStickyStack() {
    this.stickyStack?.destroy();
    this.stickyStack = null;
    const root = this.container.querySelector('.explore-view--list');
    if (root) this.stickyStack = new StickyStack(root);
  }

  /**
   * Activate the section (called when navigating to the page)
   */
  async activate() {
    // Idempotent self-guard: route handlers re-fire on URL changes (tab
    // clicks, card clicks, panel close), so skip the per-tab listener wire-
    // ups + data loads on intra-section navigations. applyURLParams still
    // runs after this returns and drives any tab/detail changes.
    if (this.isActive) return;
    console.log('[InsightsSection] Activating');

    if (!this.isInitialized) {
      this.initialize();
    }

    this.isActive = true;

    // The DOM survived deactivation but the stack was torn down with it, and
    // initialize() (hence render()) won't run again. Rebind here.
    this._initStickyStack();

    // Drop the per-tab caches on every genuine section entry, so the loads
    // below (and the lazy ones in switchTab) refetch. This surface is where
    // post-processing output lands — insights, guides, topic summaries,
    // framework results all appear after a session the user had open — so a
    // load-once cache showed a list missing everything generated since, until
    // a hard refresh. Matches the unconditional refetch every other section
    // does on activate. The `isActive` guard above still absorbs the
    // intra-section re-fires (tab clicks, card clicks, panel close), and
    // switchTab keeps its own cache guard so flipping tabs stays free.
    this.topics = [];
    this.patterns = [];
    this.guides = [];
    this.sharedGuides = [];
    this._guidesTabLoaded = false;
    this.frameworksLoaded = false;

    if (this.currentTab === 'topics') {
      this._attachTopicsTabListeners();
      if (this.topics.length === 0 && !this.topicsLoading) {
        this.loadTopicsData();
      }
    } else if (this.currentTab === 'patterns') {
      this._attachPatternsTabListeners();
      if (this.patterns.length === 0 && !this.patternsLoading) {
        this.loadPatternsData();
      }
    } else if (this.currentTab === 'guides') {
      this._attachGuidesTabListeners();
      if (this.guides.length === 0 && !this.guidesLoading) {
        this.loadGuidesData();
      }
    } else if (this.currentTab === 'assessments') {
      this._attachFrameworksTabListeners();
      if (!this.frameworksLoaded && !this.frameworksLoading) {
        this.loadFrameworksData();
      }
    }
  }

  /**
   * Deactivate the section (called when navigating away)
   */
  deactivate() {
    this.isActive = false;
    this.stickyStack?.destroy();
    this.stickyStack = null;
    if (this.orientationBanner) {
      this.orientationBanner.destroy();
      this.orientationBanner = null;
    }
    if (this.topicsFilterBar) {
      this.topicsFilterBar.destroy();
      this.topicsFilterBar = null;
    }
    if (this.filterBar) {
      this.filterBar.destroy();
      this.filterBar = null;
    }
    if (this.guidesFilterBar) {
      this.guidesFilterBar.destroy();
      this.guidesFilterBar = null;
    }
    if (this.frameworksFilterBar) {
      this.frameworksFilterBar.destroy();
      this.frameworksFilterBar = null;
    }
    this._guideRowMenu.close();
    this._disconnectTogetherObserver();
    this._disposeContentsSpy();
    if (this._panelOpen) {
      this.eventBus.emit('right-panel:close');
      this._panelOpen = false;
    }
  }

  /**
   * Re-resolve a URL detail param that may not have been resolvable on the
   * first applyURLParams pass (e.g., panel detail requested via deep link
   * before tab data loaded). Called from the end of loadTopicsData /
   * loadPatternsData / loadGuidesData / loadFrameworksData. No-op if the
   * panel is already open with the right detail or no detail param is present.
   */
  _resolvePendingDetailFromURL() {
    if (!this.isActive) return;
    const params = new URLSearchParams(window.location.search);
    const wantedTopic = params.get('topic');
    const wantedPattern = params.get('pattern');
    // `guide` only: `?sharedGuide=` is normalised to it by applyURLParams the
    // moment it is seen, so it can never still be in the URL by this point.
    const wantedGuide = params.get('guide');
    const wantedFramework = params.get('framework');
    const showingTopic = this._activeTopicId();
    const showingPattern = this._activePatternCode();
    const showingGuide = this._activeGuideId();
    const showingFramework = this._activeFrameworkId();
    if (wantedTopic && showingTopic !== wantedTopic) {
      this.applyURLParams({ topic: wantedTopic });
    } else if (wantedPattern && showingPattern !== wantedPattern) {
      this.applyURLParams({ pattern: wantedPattern });
    } else if (wantedGuide && showingGuide !== wantedGuide) {
      this.applyURLParams({ guide: wantedGuide });
    } else if (wantedFramework && showingFramework !== wantedFramework) {
      this.applyURLParams({ framework: wantedFramework });
    }
  }

  /**
   * Apply URL parameters to the section.
   *
   * Drives both tab state (?tab=) and detail panel state (?topic=, ?pattern=,
   * ?guide=, or ?framework=) from the URL. Bidirectional for the detail
   * panel:
   *  - detail param present → open or swap to that detail
   *  - all absent + panel currently showing a detail → close
   *
   * `topic` forces the tab, overriding whatever `params.tab` says — `topics`
   * is the only tab reachable with no visible nav pill, so the detail param
   * is the single source of truth for landing on it. This check runs before
   * the generic tab-switch (not as a fourth branch alongside pattern/guide/
   * framework below), so `?tab=patterns&topic=X` can't leave the section on
   * `patterns` while trying to open a topics-tab detail panel. A bare/stale
   * `?tab=topics` (no `topic=`) falls back to the default tab — there's no
   * nav pill that produces that URL, so it's treated like an unrecognized tab.
   */
  async applyURLParams(params) {
    if (params.topic) {
      if (this.currentTab !== 'topics') {
        this.handleTabChange('topics', { skipUrlPush: true });
      }
    } else if (params.tab === 'topics') {
      if (this.currentTab !== InsightsSection.DEFAULT_TAB) {
        this.handleTabChange(InsightsSection.DEFAULT_TAB, { skipUrlPush: true });
      }
    } else if (params.tab && params.tab !== this.currentTab) {
      this.handleTabChange(params.tab, { skipUrlPush: true });
    }

    if (params.topic) {
      // Dedup: same topic already shown
      if (this._panelOpen && this._panelDetail?.type === 'topic' && this._panelDetail.id === params.topic) return;
      const exists = this.topics.some(t => t.id === params.topic);
      if (exists) {
        await this.handleTopicCardClick(params.topic, { skipUrlPush: true });
      } else if (this.topics.length > 0) {
        // Topics are loaded but this id is unknown — scrub the URL.
        const url = new URL(window.location.href);
        url.searchParams.delete('topic');
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
      // If topics aren't loaded yet, the panel will be opened by the
      // post-load wiring (see loadTopicsData → applyURLParams re-entry).
    } else if (params.pattern) {
      // Dedup: same pattern already shown
      if (this._panelOpen && this._panelDetail?.type === 'pattern' && this._panelDetail.id === params.pattern) return;
      const exists = this.patterns.some(p => p.patternCode === params.pattern);
      if (exists) {
        await this.handlePatternCardClick(params.pattern, { skipUrlPush: true });
      } else if (this.patterns.length > 0) {
        // Patterns are loaded but this code is unknown — scrub the URL.
        const url = new URL(window.location.href);
        url.searchParams.delete('pattern');
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
      // If patterns aren't loaded yet, the panel will be opened by the
      // post-load wiring (see loadPatternsData → applyURLParams re-entry).
    } else if (params.guide) {
      // The one canonical guide deep link (D10). It opens whenever the merged
      // row exists — the same gate `handleViewGuide` and the card click apply
      // — so a Together-only, locked, or never-written guide is reachable by
      // link and lands on its state block rather than stranding.
      const row = this._guideRow(params.guide);
      if (this._panelOpen && this._panelDetail?.type === 'guide' && this._panelDetail.id === params.guide) {
        // Already showing it — the only thing left to honour is a pending part.
        this._scrollToGuidePart(this._takePendingGuidePart());
        return;
      }
      if (row) {
        await this.handleViewGuide(params.guide, {
          skipUrlPush: true,
          pendingPart: this._takePendingGuidePart()
        });
      } else if (this._guidesTabLoaded) {
        // Explicit loaded flag, NOT `this.guides.length > 0`: a real account
        // with zero own guides but a shared one would leave `this.guides`
        // permanently empty, so a stale `?guide=<missing>` would never scrub.
        // `_guidesTabLoaded` is set once both `loadGuidesData` fetches have
        // settled, regardless of either array's length.
        this._takePendingGuidePart();
        const url = new URL(window.location.href);
        url.searchParams.delete('guide');
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
      // Guides not loaded yet: neither open nor scrub. `_pendingGuidePart`
      // survives, so the post-load re-resolve can still land on the couple's
      // part.
    } else if (params.sharedGuide) {
      // Deprecated alias (D10). Nothing resolves it: it is rewritten to the
      // canonical `?guide=`, carrying the "the link meant the couple's part"
      // intent across the re-dispatch in `_pendingGuidePart` rather than in a
      // second URL param. `page.replace` dispatches, so the `guide` branch
      // above runs next and consumes it.
      this._pendingGuidePart = 'together';
      const url = new URL(window.location.href);
      url.searchParams.set('guide', params.sharedGuide);
      url.searchParams.delete('sharedGuide');
      page.replace(url.pathname + (url.search ? url.search : ''));
    } else if (params.framework) {
      if (this._panelOpen && this._panelDetail?.type === 'framework' && this._panelDetail.id === params.framework) return;
      const exists = this.frameworks.some(f => f.questionnaireId === params.framework);
      if (exists) {
        this.handleFrameworkCardClick(params.framework, { skipUrlPush: true });
      } else if (this.frameworksLoaded) {
        // Results are loaded (even if empty) but this id is unknown — scrub.
        const url = new URL(window.location.href);
        url.searchParams.delete('framework');
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
      // If results aren't loaded yet, the panel opens via post-load wiring
      // (loadFrameworksData → _resolvePendingDetailFromURL → applyURLParams).
    } else if (this._panelOpen && this._panelDetail) {
      this.eventBus.emit('right-panel:close');
    }
  }
}

export default InsightsSection;
